import React, { useState, useEffect, useMemo } from 'react';
import { db, storage } from '../firebase';
import { viewerFor } from '../data/testTerritory';   // Bob 2026-10-12 ("4. yes"): a tester works his EXAMPLE claims as their LC
import { viewStoredFile } from '../data/scannedFile';   // Record 591 (ruling 247): View fetches the link when clicked, after the scan
import { doc, getDoc, updateDoc, serverTimestamp, arrayUnion, arrayRemove, collection, writeBatch, onSnapshot, query, orderBy, limit, where, getDocs, addDoc, runTransaction } from 'firebase/firestore';   // runTransaction: forensic audit 2026-10-16 (set aside)
import { newOfficerNote, visibilityLine, OFFICER_NOTE_WARNING, toMs } from '../data/officerNotes';   // Record 634 (ruling 305.7)
import { annotateHistory, actorSeatLabel } from '../data/officeAtAct';   // WO-001 S8 (section 246): the office of record on the day of each act
import ClaimDocuments from './ClaimDocuments';
import MediaTracker from '../components/MediaTracker';   // Record 613 (Bob 10/8), ruling 157: the recordings tracker
import { claimCommittee, claimBasisLabel, claimYard } from '../data/claimPayload';
import { notifyAssistance } from '../data/notifications';   // ruling 89 (Record 576)
import { adminReadDraft, shouldTryAuditedRead } from '../data/adminDraftRead';   // ruling 66 (Record 582)
import useSeatsOfRecord from '../hooks/useSeatsOfRecord';   // WO-001 S3 (section 240): the chair picker and the chair of record read the spine, the bundle only until seeded
import { resolveRuleText } from '../data/ruleResolver';
import { bnClassLineFor } from '../data/bnYardmasterClass';   // Record 614 (ruling 295): Protected / ROA class of a BN rule
import { nextClaimNumber, isIssuedClaimNumber } from '../utils/claimNumber';
import { cleanEmail, emailProblem, phoneProblem } from '../data/contactCheck';
import { resolveGeneralCommittee } from '../data/routing';
import { LADDER, STAGE_ORDER as LADDER_ORDER, nextStageFor, advanceBlockedReason, canEdit, mayAttachDocuments } from '../data/claimLadder';   // canEdit: Record 592 (F24); mayAttachDocuments: forensic audit 2026-10-16
import { OUTCOMES, validateClosure, hasOutcome, outcomeLabel, CLOSING_STAGE, settlementNotice, AMOUNT_BASES, amountBasisMeta } from '../data/claimOutcome';   // AMOUNT_BASES: ruling 161   // settlementNotice: A3.4.3 (section 249)
import { mayReturn, returnWarning, returnPatch, returnRecord, REASON_MAX, frozenVersion, frozenVersionId, returnMark } from '../data/claimReturn';   // frozenVersion/returnMark: ruling 72.3 items 2, 7 (Record 583)
import {
  PRESENTERS, METHODS, presentmentPatch, presentmentSummary, isPresented, copiesWarning,
  submitConsequences, RULING_269_CONSEQUENCES,   // ruling 269: consequences stated before submit
} from '../data/claimPresentment';
import { NOTE_MAX, mayHandleRules, rulesForDisplay, addRule, setAsideRule } from '../data/claimRules';
import { claimPatch } from '../data/claimWrite';
import ClaimWorthPanel from '../components/ClaimWorthPanel';   // rulings 88 / 93 / 94 - what the claim is worth
import { ReviewPanel } from '../components/ReviewPanel';        // ruling 69 - the audited review record (G8.2)
import OfficeOfRecord from '../components/OfficeOfRecord';      // WO-001 S8 - who held the office on the filing date
import LetterGroupPanel from '../components/LetterGroupPanel';  // 6a-09 - the initial-notice letter group (ruling 130)
import ClaimFormPanel from '../components/ClaimFormPanel';
import ClaimCheckPanel from '../components/ClaimCheckPanel';   // ruling 277: the claim check, top of the page
import { isClaimantOf } from '../components/ClaimFormActions';   // ruling 70.4: the claimant records her own service   // section 512: the Penalty Time Claim itself (rulings 256-260)
import ClaimTimeline from '../components/ClaimTimeline';   // Record 690 (suggestion 6): the member's claim timeline
import CarrierDefaultBanner from '../components/CarrierDefaultBanner';   // Record 599 (section 597): the 'default claim' watch
import OffPropertyPanel from '../components/OffPropertyPanel';  // ruling 177 - the carrier answer & appeal ladder beside the status ladder (ISS-39)
import AffectedMembersPanel from '../components/AffectedMembersPanel';   // ruling 270: others affected, notified and heard
import ClaimantChannelPanel from '../components/ClaimantChannelPanel';   // T5 item 8 - ruling 29, the claimant's channel
import ExtensionRequestPanel from '../components/ExtensionRequestPanel';   // T5 item 6 - rulings 133/134
import SelfRepresentationPanel from '../components/SelfRepresentationPanel';  // ONB-6b - the §44 act (ruling 205)
import { lateArrivalFlag, NOT_A_BLOCK } from '../data/claimArrival';   // Record 680 (audit 3.6): the late-arrival flag
import CarriagePanel from '../components/CarriagePanel';
import ProgressionDecisionPanel from '../components/ProgressionDecisionPanel';   // ruling 313: the decision not to progress   // Record 594 (ruling 135 audit): who carries the claim, and her act that moves it
import { recordCarriageAtFiling } from '../data/carriageAtFiling'; import MoreSection from '../components/MoreSection'; import InclusionPanel from '../components/InclusionPanel'; import CarrierSenderPanel from '../components/CarrierSenderPanel'; import { isAssignedSenderOnly } from '../data/carrierSender'; import WorkingCopyBar from '../components/WorkingCopyBar'; import { worksInPrivate } from '../data/workingCopy'; import { addRuleActToCopy } from '../data/workingCopyStore';   // Record 609 (ruling 294): carriage recorded at filing; Record 647 (ruling 305.10): the officer's private working copy, imported on this line so no cited line moves
import RulingRef from '../components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { presenceLabel, tierRules, TIER_HEADINGS } from '../data/rulePresenceView';       // 6a-12 - an intermittent rule is labelled for the officer, never for the claimant

// BATCH 4, 2026-08-29. The ladder moved to src\data\claimLadder.js and `gc_review` was added to it
// between Doc Review and Valuation. It lives there rather than here because the authority boundary
// on it - who may see a draft, who may move a claim, and where a Local Chairperson's pen stops - is
// proved by scripts\test-claim-ladder.mjs (124 assertions) rather than eyeballed on a screen.
// A second copy of the ladder in this file would be a second source of truth for one fact (SS XI),
// so this is an import and not a declaration.
const LIFECYCLE_STAGES = LADDER;
// Section 512 (2026-09-29, Bob: "go"): the old initial-notice letter group is hidden from the claim page; the claim
// form replaces it. It stays in the code - set this true and it returns exactly as it was.
const SHOW_LETTER_GROUP = false;
const STAGE_ORDER = LADDER_ORDER;

function AddBoardAward({ claimId, user, onAdded }) {
  const [form, setForm] = useState({ boardType: 'PLB', awardNumber: '', date: '', summary: '', link: '' });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const handleSave = async () => {
    if (!form.awardNumber || !form.summary) return;
    setSaving(true); setErr('');
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        boardAwards: arrayUnion({ ...form, addedAt: new Date().toISOString() }),
        updatedAt: serverTimestamp(),
      }, user));
      onAdded(form);
      setForm({ boardType: 'PLB', awardNumber: '', date: '', summary: '', link: '' });
      setOpen(false);
    } catch (e) {
      console.error(e);
      // A rejected write must not look like a saved award (PD-5). The panel stays open
      // with the entered text intact and says what happened.
      setErr('NOT saved: ' + ((e && e.message) || 'the write was rejected') +
        '. Your entry is still here -- try again.');
    }
    finally { setSaving(false); }
  };
  if (!open) return <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setOpen(true)}>+ Add Board Award</button>;
  return (
    <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 6, padding: '12px 14px', marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <select aria-label="Board type" className="form-select" style={{ fontSize: 12, flex: 1 }} value={form.boardType} onChange={e => set('boardType', e.target.value)}>
          {['PLB','4th Division','SBA','NRAB','Other'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input aria-label="Award #" className="form-input" style={{ fontSize: 12, flex: 1 }} placeholder="Award #" value={form.awardNumber} onChange={e => set('awardNumber', e.target.value)} />
        <input aria-label="Date" className="form-input" style={{ fontSize: 12, flex: 1 }} placeholder="Date" value={form.date} onChange={e => set('date', e.target.value)} />
      </div>
      <input aria-label="Brief summary of award relevance" className="form-input" style={{ fontSize: 12, marginBottom: 8 }} placeholder="Brief summary of award relevance" value={form.summary} onChange={e => set('summary', e.target.value)} />
      <input aria-label="Link to award (optional)" className="form-input" style={{ fontSize: 12, marginBottom: 8 }} placeholder="Link to award (optional)" value={form.link} onChange={e => set('link', e.target.value)} />
      {err && (
        <div style={{ background: '#3A1414', border: '1px solid #E85C4A', borderRadius: 4, padding: '8px 12px', marginBottom: 8, fontSize: 11, color: '#FFB3A7', fontWeight: 700 }}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={handleSave} disabled={saving || !form.awardNumber || !form.summary}>{saving ? 'Saving...' : 'Save Award'}</button>
        <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}


function StageBar({ currentStage, onAdvance, canAdvance, advanceTo, blockedReason }) {
  const currentIdx = STAGE_ORDER.indexOf(currentStage);
  return (
    <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#8AA4BF', marginBottom: 12 }}>
        Claim Lifecycle
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {LIFECYCLE_STAGES.map((stage, idx) => {
          const isComplete = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const isFuture = idx > currentIdx;
          return (
            <React.Fragment key={stage.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: isComplete ? '#27AE60' : isCurrent ? '#E8A020' : '#1A2E44',
                  border: `2px solid ${isComplete ? '#27AE60' : isCurrent ? '#E8A020' : '#2E4A6B'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, transition: 'all 0.2s',
                }}>
                  {isComplete ? '✓' : stage.icon}
                </div>
                <div style={{ fontSize: 10, color: isCurrent ? '#E8A020' : isComplete ? '#27AE60' : 'var(--steel)', marginTop: 4, textAlign: 'center', fontWeight: isCurrent ? 700 : 400 }}>
                  {stage.label}
                </div>
              </div>
              {idx < LIFECYCLE_STAGES.length - 1 && (
                <div style={{ height: 2, flex: 0.5, background: idx < currentIdx ? '#27AE60' : '#2E4A6B', marginBottom: 20 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {canAdvance && currentIdx >= 0 && currentIdx < STAGE_ORDER.length - 1 && (
        <div style={{ marginTop: 14, textAlign: 'right' }}>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 16px' }} onClick={onAdvance}>
            {currentStage === 'submitted'
              ? '✓ Accept into Intake'
              : advanceTo === 'gc_review'
                ? '⬆️ Elevate to General Chairperson'
                : `Advance to ${(LIFECYCLE_STAGES.find(s => s.key === advanceTo) || {}).label || advanceTo} →`}
          </button>
        </div>
      )}

      {/* A CONTROL THAT IS SIMPLY ABSENT TEACHES NOTHING (PD-5). Where the officer cannot move this
          claim, the reason is stated in the words of the ruling that decides it - the same posture
          LCDesk takes with its unbuilt sections. An officer who elevated a claim and then went
          looking for a way to pull it back needs to be told there is no such move and why, not left
          hunting for a button that was removed. */}
      {!canAdvance && blockedReason && currentIdx >= 0 && (
        <div style={{ marginTop: 14, padding: '8px 12px', background: '#12202F',
                      border: '1px solid #2E4A6B', color: '#8AA4BF', fontSize: 11, lineHeight: 1.6 }}>
          {blockedReason}
        </div>
      )}

      {currentIdx < 0 && (
        <div style={{ marginTop: 14, textAlign: 'center', color: '#8AA4BF', fontSize: 12 }}>
          This claim is <b style={{ color: '#E8A020' }}>{currentStage}</b> and is off the
          lifecycle. The stages above are gray because the claim is not on the ladder, not
          because it has made no progress.
        </div>
      )}

      {currentIdx === STAGE_ORDER.length - 1 && (
        <div style={{ marginTop: 14, textAlign: 'center', color: '#27AE60', fontWeight: 700, fontSize: 13 }}>
          ✅ Claim Closed
        </div>
      )}
    </div>
  );
}

// RULING 86 (2026-09-14, task list section 221). The member sees the history of his own claim: what
// changed, who changed it (name and role - ruling 118), and when. Rows come from claims/{id}/history,
// written only by the claimAuditHistory trigger (functions\index.js) and readable under the rule of
// the same name. Ruling 119: an officer's working note produces NO row here - the whitelist in the
// trigger is the list of what a member may see, and nothing else reaches this list.
const ROLE_LABEL = { admin: 'Platform Administrator', general_chair: 'General Chairperson', local_chair: 'Local Chairperson', tester: 'Platform Tester', member: 'Member' };
// Keys quoted on purpose: WIN-1 in scripts\ycp-verify.js scans src\ for any `expiresAt:` writer and
// read this label map as one (cycle 221). A quoted key is a label, not an assignment, to that scan.
const FIELD_LABEL = { 'status': 'Status', 'scopeKey': 'Territory', 'committee': 'Committee', 'expiresAt': 'Filing window', 'returnReason': 'Return reason', 'returnedBy': 'Returned by', 'presentedOn': 'Presented on', 'outcome': 'Outcome', 'outcomeReason': 'Outcome reason' };
const HISTORY_RECORDED_FROM = '2026-09-14';
function ClaimHistory({ claimId, claim }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  // S8: the office spine for this claim - the General office and the Local seat, with every term of each.
  const committee = claimCommittee(claim);
  const scopeKey = claim && claim.scopeKey;
  const [spine, setSpine] = useState(null);
  useEffect(() => {
    if (!committee) { setSpine({ gcOffice: null, lcOffice: null, terms: [], scopeKey }); return undefined; }
    let dead = false;
    (async () => {
      try {
        const ids = [committee].concat(scopeKey ? [scopeKey] : []);
        const [offs, termSnaps] = await Promise.all([
          Promise.all(ids.map(id => getDoc(doc(db, 'offices', id)))),
          Promise.all(ids.map(id => getDocs(query(collection(db, 'officeTerms'), where('officeId', '==', id))))),
        ]);
        if (dead) return;
        const o = offs.filter(x => x.exists()).map(x => ({ officeId: x.id, ...x.data() }));
        setSpine({ gcOffice: o.find(x => x.officeId === committee) || null, lcOffice: scopeKey ? (o.find(x => x.officeId === scopeKey) || null) : null, terms: termSnaps.flatMap(sn => sn.docs.map(d => ({ termId: d.id, ...d.data() }))), scopeKey });
      } catch (e) { if (!dead) setSpine({ gcOffice: null, lcOffice: null, terms: [], scopeKey, err: (e && e.code) || e.message }); }
    })();
    return () => { dead = true; };
  }, [committee, scopeKey]);
  const annotated = useMemo(() => (rows && spine) ? annotateHistory(rows, spine) : null, [rows, spine]);
  useEffect(() => {
    if (!claimId) return undefined;
    const q = query(collection(db, 'claims', claimId, 'history'), orderBy('timestamp', 'desc'), limit(200));
    const unsub = onSnapshot(q, snap => { setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setErr(''); },
      e => { setErr(e && e.code === 'permission-denied' ? 'History is not available to this account.' : ('History could not be read: ' + (e && e.message))); setRows([]); });
    return () => unsub();
  }, [claimId]);
  const show = (v) => (v === null || v === undefined || v === '') ? '—' : (typeof v === 'object' ? (('count' in v) ? v.count + ' item(s)' : JSON.stringify(v)) : String(v));
  return (
    <Section title="History">
      <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 10 }}>
        Every recorded change to this claim, who made it and when. Officers' working notes are not part of this record. History recorded from {HISTORY_RECORDED_FROM}. Beside each act: who held the General office and the Local seat THAT DAY, from the term record (WO-001 S8) - so a claim acted on last year reads as acted on by last year's chairperson.
      </div>
      {annotated && annotated.flagged ? <div className="alert-banner">&#9888; {annotated.flagged} act(s) were made by an officer the term record does not name as the holder of record that day. Each is marked in the table; the act stands, the seat is not established by the spine.</div> : null}
      {spine && spine.err ? <div style={{ fontSize: 12, color: '#E06C6C' }}>&#9888; The office record could not be read ({spine.err}); acts are shown without their office of record.</div> : null}
      {annotated && annotated.holders.length ? (
        <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 8 }}>Offices that held this claim across its life: {annotated.holders.map(h => (h.acting ? 'Acting ' : '') + (h.kind === 'gc' ? 'GC ' : 'LC ') + h.holderName + ' (' + h.termId + ', from the act of ' + h.firstActOn + ')').join(' · ')}</div>
      ) : null}
      {err && <div className="alert-banner">&#9888; {err}</div>}
      {rows === null && !err && <div style={{ fontSize: 13, color: '#8AA4BF' }}>Loading…</div>}
      {rows && rows.length === 0 && !err && <div style={{ fontSize: 13, color: '#8AA4BF' }}>No changes recorded yet.</div>}
      {rows && rows.length > 0 && (
        <div className="table-wrapper">
          <table>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>From</th><th>To</th><th>Reason</th><th>Office of record that day</th></tr></thead>
            <tbody>
              {(annotated ? annotated.acts : rows).map(h => (
                <tr key={h.id} style={h.office && h.office.flag ? { outline: '1px solid #E8A020' } : undefined}>
                  <td>{h.timestamp ? new Date(h.timestamp).toLocaleString() : '—'}</td>
                  <td>{h.actorName ? (h.actorName + (h.actorRole ? ', ' + (ROLE_LABEL[h.actorRole] || h.actorRole) : '')) : (h.actorRole ? (ROLE_LABEL[h.actorRole] || h.actorRole) : (h.actor || 'unknown'))}{h.office ? <div style={{ fontSize: 11, color: h.office.flag ? '#E8A020' : '#8AA4BF' }}>{actorSeatLabel(h.office.actorSeat)}</div> : null}</td>
                  <td>{FIELD_LABEL[h.field] || h.field}</td>
                  <td>{show(h.before)}</td>
                  <td>{show(h.after)}</td>
                  <td>{h.reason || ''}</td>
                  <td style={{ fontSize: 11 }}>{h.office ? <>{h.office.gcLine}<br />{h.office.lcLine}{h.office.flag ? <div style={{ color: '#E8A020', marginTop: 2 }}>&#9888; {h.office.flag}</div> : null}</> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Field({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: highlight ? '#E8A020' : '#F4F6F8', fontWeight: highlight ? 700 : 400 }}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">{title}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {children}
      </div>
    </div>
  );
}

export default function ClaimDetail({ claimId, user: signedIn, onNavigate, onResumeDraft }) {
  const [claim, setClaim] = useState(null);
  // Bob 2026-10-12 ("4. yes"): on his own EXAMPLE claim a tester works it as its Local Chairperson (testTerritory.viewerFor);
  // to every other claim he is exactly who he is.
  // Forensic audit 2026-10-16: viewerFor() returns a NEW object for a tester on his example, so `user` changed identity
  // on every render and the load effect (keyed on it) re-read the claim, set it, re-rendered, and re-read - an endless
  // reload loop. The viewer is memoized on the only claim fields viewerFor reads (status, isExample, scopeKey,
  // generalCommittee), and the load itself is keyed on the signed-in person, never on the derived viewer.
  const viewStatus = claim ? claim.status : null;
  const viewIsExample = claim ? claim.isExample : null;
  const viewScopeKey = claim ? claim.scopeKey : null;
  const viewGeneralCommittee = claim ? claim.generalCommittee : null;
  const user = useMemo(
    () => viewerFor(signedIn, claim ? { status: viewStatus, isExample: viewIsExample, scopeKey: viewScopeKey, generalCommittee: viewGeneralCommittee } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signedIn, viewStatus, viewIsExample, viewScopeKey, viewGeneralCommittee]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');   // the LOAD failed - the only error that replaces the page
  // Forensic audit 2026-10-16: an action that fails (advance, note, contact, notice date) used to set `error` above,
  // which replaced the whole page with one red line and threw away what the officer had typed. Action errors are
  // shown beside the control that failed: { at: 'advance' | 'note' | 'contact' | 'notice', text }.
  const [actionError, setActionError] = useState({ at: '', text: '' });
  const actionErrorAt = (at) => (actionError.at === at && actionError.text ? (
    <div role="alert" style={{ background: '#3A1414', border: '1px solid #E85C4A', borderRadius: 4, padding: '8px 12px', margin: '8px 0', fontSize: 12, color: '#FFB3A7', fontWeight: 700 }}>
      ⚠ {actionError.text}
    </div>
  ) : null);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Record 634 (ruling 305.7): officer notes live in claims/{id}/officerNotes. An officer reads them all; the member reads
  // only those the server has shown her (visibleToMember) - the query must say so or the rules refuse the list.
  const [officerNoteRows, setOfficerNoteRows] = useState([]);
  const [officerNotesError, setOfficerNotesError] = useState('');   // Forensic audit 2026-10-16: a failed read is said, not shown as "no notes"
  const [shareNow, setShareNow] = useState(false);
  const writesOfficerNotes = !!user && (user.isAdmin === true || ['admin', 'general_chair', 'local_chair'].includes(user.role));
  const claimStatusForNotes = claim ? claim.status : null; const claimantIncludedForNotes = !!(claim && claim.claimantIncluded === true);   // Record 652 (ruling 305.12)
  const hasViewer = !!user;
  // Forensic audit 2026-10-16: keyed on whether there is a viewer, not on the viewer object - the listener was torn
  // down and re-subscribed on every render. Everything the query depends on is already a primitive in this list.
  useEffect(() => {
    if (!claimId || !hasViewer || !claimStatusForNotes || claimStatusForNotes === 'draft') { setOfficerNoteRows([]); setOfficerNotesError(''); return undefined; }
    const col = collection(db, 'claims', claimId, 'officerNotes');
    const q = (writesOfficerNotes || claimantIncludedForNotes) ? col : query(col, where('visibleToMember', '==', true));   // Record 652: an included claimant reads them all
    return onSnapshot(q, snap => { setOfficerNoteRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setOfficerNotesError(''); },
      e => { setOfficerNoteRows([]); setOfficerNotesError('Officer notes could not be read' + (e && (e.code || e.message) ? ' (' + (e.code || e.message) + ')' : '') + '. This is not the same as "no notes".'); });
  }, [claimId, hasViewer, claimStatusForNotes, writesOfficerNotes, claimantIncludedForNotes]);
  const [advancing, setAdvancing] = useState(false);
  // Batch 5: how the claim ended. Never pre-filled (61.9a) - a blank must be visibly a blank
  // rather than a default nobody read.
  const [closureOpen, setClosureOpen] = useState(false);
  const [settleNoticeOn, setSettleNoticeOn] = useState('');   // A3.4.3 (section 249)
  const [closure, setClosure] = useState({ outcome: '', outcomeReason: '', amountRecovered: '', amountBasis: '', boardCaseNumber: '' });   // amountBasis: ruling 161
  const [closureErrors, setClosureErrors] = useState([]);
  const [closing, setClosing] = useState(false);

  // RULING 72 - the return to its author. State only; every decision is in src\data\claimReturn.js,
  // proved by scripts\test-claim-return.mjs (63 assertions). This file renders and writes.
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnErrors, setReturnErrors] = useState([]);
  const [returning, setReturning] = useState(false);
  const [returnedNotice, setReturnedNotice] = useState(false);

  // RULING 70 - presentment. NOTE WHAT IS NOT HERE: no permission state, because there is no
  // permission. 70.2 withdrew the authority model - "presentment is an event to be recorded, not an
  // authority to be enforced" - so every officer and the member alike get this control.
  const [presentOpen, setPresentOpen] = useState(false);
  const [present, setPresent] = useState({ presentedBy: '', presentedByName: '', presentedOn: '', method: '', methodOther: '', copiesTo: '', note: '', proof: '' });
  const [presentErrors, setPresentErrors] = useState([]);
  const [presenting, setPresenting] = useState(false);

  // RULING 13 - rule handling. ADD and SET ASIDE. There is no third control because there is no
  // third verb: a rule considered and set aside stays on the claim, visibly, so that at a board
  // "was this theory ever looked at?" is answerable from the record.
  const [ruleForm, setRuleForm] = useState({ open: '', ruleId: '', ruleLabel: '', note: '' });
  const [ruleErrors, setRuleErrors] = useState([]);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [filing, setFiling] = useState(false);
  const [fileError, setFileError] = useState('');
  const [suspendModal, setSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [routeTo, setRouteTo] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [showAllChairs, setShowAllChairs] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [formModel, setFormModel] = useState(null);   // ruling 277: ClaimFormPanel's model, read by the claim check
  const [editingContact, setEditingContact] = useState(false);
  // Record 593 (ruling 205 audit): the served §44 notice's words while THIS officer has not acknowledged it, else ''.
  // Set by SelfRepresentationPanel (onAckState). While set, edit / send back / advance are held and say why.
  const [s44Block, setS44Block] = useState('');
  const [contactForm, setContactForm] = useState({});

  useEffect(() => {
    if (!claimId) return;
    setLoading(true);
    // Ruling 66 (Record 582): an administrator refused a direct read of another person's draft reads it
    // through the audited callable; every such read is logged to audit_logs.
    getDoc(doc(db, 'claims', claimId))
      .catch(e => {
        if (!shouldTryAuditedRead(signedIn, e)) throw e;   // the administrator's own account - viewerFor never changes one
        return adminReadDraft('claim', claimId).then(d => ({ id: d.id, exists: () => true, data: () => d }));
      })
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          setClaim({ id: snap.id, ...data });
          setDocuments(data.documents || []);
          setContactForm({
            claimantStreet: data.claimantStreet || '',
            claimantCity: data.claimantCity || '',
            claimantState: data.claimantState || '',
            claimantZip: data.claimantZip || '',
            claimantPhone: data.claimantPhone || '',
            claimantEmail: data.claimantEmail || '',
          });
        } else {
          setError('Claim not found.');
        }
      })
      .catch(e => setError('Error loading claim: ' + e.message))
      .finally(() => setLoading(false));
  }, [claimId, signedIn]);   // Forensic audit 2026-10-16: NOT the derived viewer - setClaim below would re-key this effect forever

  // BATCH 4. `canAdvance` used to be a ROLE TEST - admin, GC or LC, true regardless of the claim -
  // which meant the button rendered on a claim the officer had no authority to move, and the
  // failure surfaced as a rejected write rather than an absent control. It is now a question about
  // THIS claim and THIS user, answered by the module the suite proves. A Local Chairperson is
  // offered nothing at or past gc_review (27c), and nobody is offered anything on a draft (56).
  const advanceTo = claim ? nextStageFor(claim, user) : null;
  const canAdvance = !!advanceTo && !s44Block;   // Record 593 (ruling 205 audit)
  const advanceBlocked = s44Block
    ? 'Held until you acknowledge the member\'s §44 notice in the Representation panel below. ' + s44Block
    : (claim ? advanceBlockedReason(claim, user) : '');

  // AND THIS IS THE ROLE TEST `canAdvance` USED TO BE, KEPT DELIBERATELY.
  //
  // Suspend/Archive and Reactivate are OFFICE powers, not advance powers, and they were riding on
  // the old role-shaped `canAdvance`. Narrowing them to the new claim-specific test would have
  // silently removed abilities officers have today: an officer would lose Suspend/Archive on a
  // claim at the last rung, and - worse - Reactivate would never render at all, because an ARCHIVED
  // claim is off the ladder and nextStageFor() correctly returns null for it. That is precisely the
  // harm Bob's standing test of 2026-08-26 forbids: "don't do any harm to the end user's abilities".
  //
  // So these two keep the exact test they had. Whether an out-of-scope officer should see them at
  // all is a real question and a SEPARATE change; it is not smuggled in behind an elevation batch.
  const isOfficer = user?.isAdmin || user?.role === 'general_chair' || user?.role === 'local_chair';
  // Record 592 (functions audit F23): the question the paragraph above left open is answered by the rules, not here.
  // lcHolds()/lcSettable() contain neither 'suspended' nor 'archived', so an LC's Suspend/Archive and Reactivate were
  // always refused - the control was offered and failed silently. They are offered to whom the rules admit them:
  // the GC of the claim's committee and the administrator, on a filed claim. Nobody loses an ability he had.
  const maySuspend = !!claim && claim.status !== 'draft'
    && (!!user?.isAdmin || (user?.role === 'general_chair' && !!user?.committee && user.committee === claimCommittee(claim)));
  // Record 592 (F24): notes, contact and board awards are offered where an update arm admits them (claimLadder.canEdit,
  // the client mirror of claimantEdit/lcEdit/gcEdit/adminEdit). A member's filed claim and an LC's claim past his rung
  // took the typing and then refused it.
  const mayWrite = claim ? canEdit(claim, user) : false;
  const canRoute = user?.isAdmin || user?.role === 'general_chair';
  const seatsOfRecord = useSeatsOfRecord(claimCommittee(claim) || (user && user.committee));
  const [filingCarriage, setFilingCarriage] = useState(null);   // Record 609 (ruling 294): carriage recorded at filing
  const isUnrouted = claim?.routingStatus === 'unrouted' || String(claim?.scopeKey || '').endsWith('|UNROUTED');

  // Smart reassign — narrow the chair picker to those whose craft + railroad match this claim.
  const LC_ROSTER = seatsOfRecord.roster;   // S3: same name, so nothing below changes meaning; the source is the spine
  const rosterMatches = claim ? LC_ROSTER.filter(lc => lc.craft === claim.craft && lc.railroadKey === claim.railroad) : [];
  const narrowedByClaim = rosterMatches.length > 0 && rosterMatches.length < LC_ROSTER.length;
  const chairOptions = (showAllChairs || rosterMatches.length === 0) ? LC_ROSTER : rosterMatches;

  // The Local Chairperson is a PROJECTION of this claim's scopeKey through the roster --
  // never a stored name. The roster label already carries [UNCONFIRMED] for an unattested
  // chair and the vacancy text for an empty seat, so the record cannot assert a holder the
  // committee has not attested (PD-4).
  const lcForClaim = (() => {
    const sk = String(claim?.scopeKey || '');
    if (!sk || sk.endsWith('|UNROUTED')) return 'Not yet assigned - pending routing by the General Chair';
    const hit = LC_ROSTER.find(lc => lc.scopeKey === sk);
    if (!hit) return sk + ' - no chair of record';
    const inParens = hit.label.match(/\(([^)]*)\)\s*$/);
    return inParens ? inParens[1] : hit.label;
  })();

  // Pre-select the sole matching chair for an unrouted claim.
  useEffect(() => {
    if (isUnrouted && rosterMatches.length === 1 && !routeTo) setRouteTo(rosterMatches[0].scopeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnrouted, claim]);

  const handleSuspend = async (newStatus) => {
    setSuspending(true);
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        status: newStatus,
        suspendReason: suspendReason || (newStatus === 'archived' ? 'Archived by ' + user?.name : 'Suspended pending further action'),
        suspendedAt: serverTimestamp(),
        suspendedBy: user?.name || user?.email,
        updatedAt: serverTimestamp(),
      }, user));
      setClaim(prev => ({ ...prev, status: newStatus }));
      setSuspendModal(false);
      setSuspendReason('');
    } catch (e) {
      console.error('Suspend error:', e);
      window.alert('The claim was NOT ' + (newStatus === 'archived' ? 'archived' : 'suspended') + ': ' + ((e && e.message) || 'the write was refused'));   // Record 592 (F23)
    } finally {
      setSuspending(false);
    }
  };

  const handleReactivate = async () => {
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        status: 'intake',
        suspendReason: null,
        reactivatedAt: serverTimestamp(),
        reactivatedBy: user?.name || user?.email,
        updatedAt: serverTimestamp(),
      }, user));
      setClaim(prev => ({ ...prev, status: 'intake' }));
    } catch (e) {
      console.error('Reactivate error:', e);
      window.alert('The claim was NOT reactivated: ' + ((e && e.message) || 'the write was refused'));   // Record 592 (F23)
    }
  };

  // GC routing: assign an unrouted (or misrouted) claim to a Local Chairperson.
  // committee is unchanged — gcEdit permits scopeKey changes within the committee.
  const handleReassign = async () => {
    if (!routeTo) return;
    setReassigning(true);
    try {
      // `committee` must travel with the scopeKey. Claims.js:51 and Dashboard.js:71 both read
      // claims by committee, so a re-routed claim left with an empty or stale committee
      // disappears from the General Chair's own list while remaining perfectly valid.
      // Derived, never typed; left alone if the key will not parse (SS XI).
      const derivedCommittee = resolveGeneralCommittee(routeTo);
      const patch = {
        scopeKey: routeTo,
        routingStatus: 'routed',
        manualRoute: true,
        manualRouteAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (derivedCommittee) patch.committee = derivedCommittee;
      await updateDoc(doc(db, 'claims', claimId), claimPatch(patch, user));
      setClaim(prev => ({ ...prev, ...patch, scopeKey: routeTo, routingStatus: 'routed', manualRoute: true }));
      setRouteTo('');
    } catch (e) {
      console.error('Reassign error:', e);
      alert('Could not route this claim: ' + e.message);
    } finally {
      setReassigning(false);
    }
  };

  // RECORD HOW IT ENDED, then close. One write: outcome, reason, amount and status together, so a
  // claim can never sit at `closure` with no outcome because the second write failed.
  const handleClose = async () => {
    const v = validateClosure(closure);
    setClosureErrors(v.errors);
    if (!v.ok) return;
    setClosing(true);
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        ...v.patch,
        status: CLOSING_STAGE,
        outcomeRecordedAt: serverTimestamp(),
        outcomeRecordedBy: user?.name || user?.email || 'Unknown',
        outcomeRecordedByRole: user?.role || 'unknown',
        notes: arrayUnion({
          text: 'Closed as ' + outcomeLabel(v.patch.outcome) + '. ' + v.patch.outcomeReason,
          addedBy: user?.name || user?.email || 'Unknown',
          role: user?.role || 'unknown',
          addedById: user?.uid || '',
          addedAt: new Date().toISOString(),
        }),
        updatedAt: serverTimestamp(),
      }, user));
      // Forensic audit 2026-10-16: the local copy carries the recording date too. Without it settlementNotice() fell
      // back to the claim's OLD updatedAt and, right after closing as Settled, told the officer the notice was overdue.
      // The server's own stamp replaces this on the next read.
      const recordedNow = new Date().toISOString();
      setClaim(prev => ({ ...prev, ...v.patch, status: CLOSING_STAGE, outcomeRecordedAt: recordedNow, outcomeRecordedBy: user?.name || user?.email || 'Unknown' }));
      setClosureOpen(false);
      setClosureErrors([]);
    } catch (e) {
      // A rejected write must never look like a recorded outcome (PD-5). The panel stays open with
      // the officer's text intact.
      setClosureErrors(['NOT saved: ' + ((e && e.message) || 'the write was rejected')
                      + '. Your entry is still here - the claim is NOT closed.']);
    } finally { setClosing(false); }
  };

  // ------------------------------------------------------------------ RULING 13: RULE HANDLING
  //
  // ONE BATCH, AND THE BATCH IS THE RULING. The workspace requirements SS3.1: "An LC annotation
  // writes, atomically: the annotation, a notification to the claimant, a notification to the GC.
  // IF THE NOTIFICATION FAILS, THE ANNOTATION FAILS." A Firestore writeBatch is atomic, so a denied
  // notification takes the annotation down with it - which is what makes "must follow up" true
  // rather than aspirational. Two sequential updateDoc calls would satisfy the sentence and fail
  // the purpose.
  const canHandleRules = claim ? mayHandleRules(claim, user) : false;

  const handleRuleAction = async () => {
    const verb = ruleForm.open;
    const r = verb === 'add'
      ? addRule(claim, user, { ruleId: ruleForm.ruleId, ruleLabel: ruleForm.ruleLabel, note: ruleForm.note })
      : setAsideRule(claim, user, { ruleId: ruleForm.ruleId, note: ruleForm.note });
    setRuleErrors(r.errors);
    if (!r.ok) return;
    setRuleSaving(true);
    try { if (worksInPrivate(user, claim)) { const w = await addRuleActToCopy(claimId, claim, user, { verb: verb === 'add' ? 'add' : 'set_aside', ruleId: ruleForm.ruleId, ruleLabel: ruleForm.ruleLabel, note: ruleForm.note }); if (!w.ok) { setRuleErrors(w.errors); return; } setRuleForm({ open: '', ruleId: '', ruleLabel: '', note: '' }); setRuleErrors([]); return; }   // Record 647 (ruling 305.10): an officer's rule act goes to his working copy; Publish writes it and ruling 13's notices
      const claimRef = doc(db, 'claims', claimId);
      let written = r;

      if (verb === 'add') {
        const batch = writeBatch(db);
        batch.update(claimRef, claimPatch({
          matchedRules: arrayUnion(r.rulesPatch),
          updatedAt: serverTimestamp(),
        }, user));
        r.notifications.forEach(n => {
          batch.set(doc(collection(db, 'notifications')), { ...n, createdAt: serverTimestamp() });   // Record 592 (F14): the server's clock
        });
        await batch.commit();
      } else {
        // The whole array, with one entry stamped. arrayUnion cannot edit an element in place.
        // Forensic audit 2026-10-16: that whole array was built from THIS PAGE'S copy of the claim, so a rule another
        // officer added since the page loaded was silently dropped by the write. The array is now read and stamped
        // inside a transaction: the read is the stored list, and Firestore retries if it changes before the commit.
        // The notifications ride in the same transaction, so ruling 13's all-or-nothing holds as it did in the batch.
        written = await runTransaction(db, async (tx) => {
          const snap = await tx.get(claimRef);
          if (!snap.exists()) throw new Error('the claim could not be read');
          const fresh = setAsideRule({ ...claim, ...snap.data(), id: claimId }, user, { ruleId: ruleForm.ruleId, note: ruleForm.note });
          if (!fresh.ok) return fresh;
          tx.update(claimRef, claimPatch({ ...fresh.rulesPatch, updatedAt: serverTimestamp() }, user));
          fresh.notifications.forEach(n => {
            tx.set(doc(collection(db, 'notifications')), { ...n, createdAt: serverTimestamp() });   // Record 592 (F14)
          });
          return fresh;
        });
        if (!written.ok) { setRuleErrors(written.errors); return; }
      }

      setClaim(prev => (verb === 'add'
        ? { ...prev, matchedRules: [...(prev.matchedRules || []), r.rulesPatch] }
        : { ...prev, ...written.rulesPatch }));
      setRuleForm({ open: '', ruleId: '', ruleLabel: '', note: '' });
      setRuleErrors([]);
    } catch (e) {
      // The batch is all-or-nothing, so this message can say so truthfully.
      setRuleErrors(['NOTHING was saved: ' + ((e && e.message) || 'the write was rejected')
                   + '. The rule was NOT changed and no notification was sent - the annotation and '
                   + 'its notifications are one write, so a failure leaves the claim exactly as it was.']);
    } finally { setRuleSaving(false); }
  };

  // ------------------------------------------------------------------ RULING 70: PRESENTMENT
  const handlePresent = async () => {
    // RULING 257 (section 517): a claim is not recorded as served until a SIGNED copy is saved with the case.
    // Correcting an existing record is never blocked - claims served before this rule keep their record.
    if (!isPresented(claim) && !(documents || []).some(d => d && d.signedCopy)) {
      // Forensic audit 2026-10-16: "attach it below" was said to everyone, but only someone mayAttachDocuments() admits
      // is offered the upload - the claimant of a claim an officer filed for her is not its creator and has no control
      // there (ownerAttachesDocuments). Each is told the step that is actually open to them; the rules are unchanged.
      const officerViewer = !!(user && (user.isAdmin || user.role === 'local_chair' || user.role === 'general_chair'));
      setPresentErrors([mayAttachDocuments(claim, user)
        ? 'Save the signed copy of the claim to Documents first - attach it below and tick "This copy is the signed claim". Signed copies are always saved with the case.'
        : officerViewer
          ? 'Save the signed copy of the claim to Documents first. This claim is not at a stage where you can attach documents to it, so the officer who holds it now saves the signed copy. Signed copies are always saved with the case.'
          : 'Save the signed copy of the claim to Documents first. On this claim you cannot attach documents yourself - ask your Local Chairperson to save your signed copy to the claim\'s Documents. Signed copies are always saved with the case.']);
      return;
    }
    const pin = { ...present, copiesTo: String(present.copiesTo || '').split(',').map(x => x.trim()).filter(Boolean) };
    // Ruling 269: the consequence sentences shown on the form are recorded with the service - the same list, computed
    // from the same input, so what is recorded is exactly what was on screen.
    // Bob 2026-10-12: the one who submits signs; the form's signer block is fixed on the claim with the service.
    const r = presentmentPatch(claim, user, { ...pin, consequencesShown: submitConsequences(claim, pin), signer: formModel && formModel.signer });
    setPresentErrors(r.errors);
    if (!r.ok) return;
    setPresenting(true);
    try {
      // RULING 70.4: a claimant (not an officer) records service through firestore.rules claimantRecordsService(),
      // which admits the presentment fields and nothing else - so her write carries no note; the presentment fields
      // themselves (who served, when, how, who recorded it and when) are the record.
      const claimantOnly = (isClaimantOf(claim, user) && !(user && (user.isAdmin || user.role === 'local_chair' || user.role === 'general_chair'))) || isAssignedSenderOnly(claim, user);   // Record 654: the named sender's path is the claimant's narrow one
      await updateDoc(doc(db, 'claims', claimId), claimPatch(claimantOnly ? { ...r.patch, updatedAt: serverTimestamp() } : {
        ...r.patch,
        // NOT WRITTEN, AND BOTH ABSENCES ARE THE RULING: `status` - serving the carrier is not
        // advancing the internal stage. `expiresAt` - the window is anchored to the occurrence.
        notes: arrayUnion({
          text: 'Presentment recorded: ' + presentmentSummary({ ...claim, ...r.patch }).text
              + (r.patch.presentmentWarnings ? ' Recorded after these were stated: ' + r.patch.presentmentWarnings.shown.map(x => x.text).join(' ') : ''),   // Record 589 (audit), ruling 168: the member reads the notes; ruling 269's number is not stored in them
          addedBy: user?.name || user?.email || 'Unknown',
          role: user?.role || 'unknown',
          addedById: user?.uid || '',
          addedAt: new Date().toISOString(),
        }),
        updatedAt: serverTimestamp(),
      }, user));
      setClaim(prev => ({ ...prev, ...r.patch }));
      setPresentOpen(false);
      setPresentErrors([]);
    } catch (e) {
      setPresentErrors(['NOT recorded: ' + ((e && e.message) || 'the write was rejected')
                      + '. Your entry is still here - nothing has been saved.']);
    } finally { setPresenting(false); }
  };

  // ------------------------------------------------------------------ RULING 72: THE RETURN
  //
  // WHO MAY, AND THE WARNING, ARE BOTH IMPORTED. Nothing about the return is decided in this file:
  // mayReturn() answers whether the control renders at all (an LC below gc_review, or the GC of the
  // committee - the administrator is deliberately excluded under ruling 66), and returnWarning()
  // supplies the clock warning as DATA so the panel can show it BEFORE the officer commits rather
  // than after. 72.3 item 5 warns hard and names the date; it does not forbid.
  const canReturn = claim ? mayReturn(claim, user) : false;
  const returnWarn = claim ? returnWarning(claim) : { level: 'ok', text: '' };

  const handleReturn = async () => {
    if (s44Block) return;   // Record 593 (ruling 205 audit): the control is held, and so is its handler
    const r0 = returnPatch(claim, user, { reason: returnReason });
    setReturnErrors(r0.errors);
    if (!r0.ok) return;
    setReturning(true);
    try {
      // Ruling 72.3 item 2 (Record 583): the frozen copy must equal the claim EXACTLY as it stands (firestore.rules
      // compares it to the stored document), so it is read fresh here, and the return is computed from that read.
      const fresh = await getDoc(doc(db, 'claims', claimId));
      if (!fresh.exists()) throw new Error('the claim could not be read');
      const freshData = fresh.data();
      const r = returnPatch({ ...claim, ...freshData }, user, { reason: returnReason });
      if (!r.ok) { setReturnErrors(r.errors); return; }
      // Section 488: the claim and the officer's content-free record go in ONE batch, so neither exists
      // without the other. firestore.rules checks the record against the claim as it stands after the batch.
      const batch = writeBatch(db);
      // Record 588 (ruling 202): built from the fresh read, so the claimant keys equal the stored claim's (the rule checks).
      batch.set(doc(db, 'claimReturns', claimId), returnRecord({ ...claim, ...freshData }, r.patch, user, claimId));
      batch.set(doc(db, 'claims', claimId, 'versions', frozenVersionId(r.patch.returnCount)),
                frozenVersion(freshData, r.patch, user, serverTimestamp()));   // SUPERSEDED, never VOID (46.7)
      batch.update(doc(db, 'claims', claimId), claimPatch({
        ...r.patch,
        // NOT IN THE PATCH AND NOT ADDED HERE: expiresAt. 72.3 item 6 - the window is NEVER
        // recomputed, and its absence from this write is the guarantee.
        notes: arrayUnion({
          text: 'Returned to its author. ' + r.patch.returnReason,
          addedBy: user?.name || user?.email || 'Unknown',
          role: user?.role || 'unknown',
          addedById: user?.uid || '',
          addedAt: new Date().toISOString(),
        }),
        updatedAt: serverTimestamp(),
      }, user));
      await batch.commit();
      // The claim is now a draft, and under ruling 56 a draft is beyond everyone but its author -
      // so this officer is about to lose read on the page he is standing on. Say so plainly rather
      // than letting the next render fail as a permission error.
      setReturnOpen(false);
      setReturnErrors([]);
      setReturnedNotice(true);
      setClaim(prev => ({ ...prev, ...r.patch }));
      setReturnReason('');
    } catch (e) {
      // A rejected write must never look like a completed return (PD-5). The reason stays on screen.
      setReturnErrors(['NOT sent back: ' + ((e && e.message) || 'the write was rejected')
                     + '. Your reason is still here - the claim has NOT moved.']);
    } finally { setReturning(false); }
  };

  const handleAdvance = async () => {
    if (s44Block) return;   // Record 593 (ruling 205 audit)
    const currentIdx = STAGE_ORDER.indexOf(claim.status);
    // 'suspended' and 'archived' are not on the ladder, so indexOf returns -1 and
    // STAGE_ORDER[-1 + 1] is 'draft'. Without this guard, advancing an ARCHIVED claim
    // silently returns it to Draft, contradicting the modal that promised two clicks
    // earlier that archiving cannot be reactivated (PD-5).
    if (currentIdx < 0) return;
    if (currentIdx >= STAGE_ORDER.length - 1) return;
    // BATCH 4: the target comes from nextStageFor() - the same function that decided whether to
    // render the button - rather than being recomputed as index+1 here. Two computations of one
    // decision drift; this one would have let a Local Chairperson advance past gc_review if the
    // button were ever rendered by mistake. The rules would refuse it, but a UI that ASKS for a
    // move it is not entitled to make is still a defect, and the officer would see a rejection
    // rather than an explanation.
    const nextStage = advanceTo;
    if (!nextStage) return;

    // BATCH 5, RULING 61.9a. A claim cannot reach `closure` without a recorded outcome AND a
    // reason. This is the gate at the moment the officer actually meets it - not a validation on a
    // form he can navigate away from. It is deliberately here rather than only in the modal,
    // because the modal is one path to closure and the stage bar is another.
    //
    // Nothing is pre-filled and nothing is inferred (68.2): if he has not said how it ended, the
    // claim does not close, and it certainly is not recorded as a denial by default.
    if (nextStage === CLOSING_STAGE && !hasOutcome(claim)) {
      setClosureOpen(true);
      return;
    }
    setAdvancing(true);
    setActionError({ at: '', text: '' });
    try {
      const updates = {
        status: nextStage, ...(nextStage === 'gc_review' ? { gcForwardCount: (Number(claim.gcForwardCount) || 0) + 1 } : {}),   // Record 651 (ruling 305.11): the forward to the GC is a hand-off - the next version
        updatedAt: serverTimestamp(),
      };
      if (claim.status === 'submitted' && nextStage === 'intake') {
        updates.acceptedAt = serverTimestamp();
        updates.acceptedBy = user?.name || user?.email || 'Unknown';
      }
      await updateDoc(doc(db, 'claims', claimId), claimPatch(updates, user));
      setClaim(prev => ({ ...prev, status: nextStage }));
    } catch (e) {
      setActionError({ at: 'advance', text: 'The claim was NOT advanced: ' + ((e && e.message) || 'the write was refused') });   // Forensic audit 2026-10-16: beside the control, not over the page
    } finally {
      setAdvancing(false);
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    setActionError({ at: '', text: '' });
    try {
      // Record 634 (ruling 305.7): an officer's note is his union's until the server shows it to the member.
      if (writesOfficerNotes) {
        const r = newOfficerNote(user, note, shareNow, serverTimestamp());
        if (!r.ok) { setActionError({ at: 'note', text: 'Note NOT saved: ' + r.errors.join(' ') }); return; }   // Forensic audit 2026-10-16: the typed note stays
        await addDoc(collection(db, 'claims', claimId, 'officerNotes'), r.doc);
        setNote(''); setShareNow(false);
        setNoteSuccess(true);
        setTimeout(() => setNoteSuccess(false), 3000);
        return;
      }
      const newNote = {
        text: note.trim(),
        addedBy: user?.name || user?.email || 'Unknown',
        // The renderer badges on n.role: 'general_chair' prints General Chair, 'local_chair' prints
        // Local Chair, anything else prints Member. The role was NEVER WRITTEN here, so every note
        // saved through this handler rendered as a MEMBER note whoever wrote it -- a General
        // Chairperson's binding directive displayed under a Member badge. Write what the renderer
        // has always read.
        role: user?.role || 'member',
        addedById: user?.uid || '',
        addedAt: new Date().toISOString(),
      };
      // arrayUnion -- NOT a read-modify-write of the whole array.
      //
      // `notes: [...existing, newNote]` rewrote the ENTIRE note history on every save, from a local
      // copy that may already have been stale. Two officers adding a note to the same claim minutes
      // apart and the second silently erased the first. And any writer could drop the history
      // wholesale with no rule able to stop it: an array is ONE FIELD, and the rules layer cannot
      // see inside it.
      //
      // Ruling 58 (2026-08-28) makes this load-bearing - communications "should be digitally and
      // saved recorded". A channel a party can silently rewrite is not a record of what was said;
      // it is a record of what somebody last wanted it to say.
      //
      // boardAwards (line 36) and documents (ClaimDocuments.js) already used arrayUnion. Notes was
      // the one that did not.
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        notes: arrayUnion(newNote),
        updatedAt: serverTimestamp(),
      }, user));
      setClaim(prev => ({ ...prev, notes: [...(prev.notes || []), newNote] }));
      setNote('');
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 3000);
    } catch (e) {
      setActionError({ at: 'note', text: 'Note NOT saved: ' + ((e && e.message) || 'the write was refused') + '. Your note is still in the box - try again.' });
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveContact = async () => {
    if (s44Block) return;   // Record 593 (ruling 205 audit)
    // Bob 2026-10-14 (the walk-through): a trailing period on the e-mail defeated the link to the member's account, and a
    // phone with letters in it was saved. The e-mail is stored cleaned; a bad e-mail or phone is refused in plain words.
    const cleaned = { ...contactForm, ...(contactForm.claimantEmail !== undefined ? { claimantEmail: cleanEmail(contactForm.claimantEmail) } : {}) };
    // Forensic audit 2026-10-16: the claimant's e-mail is what links the claim to her account, so firestore.rules
    // claimantLinkIntact() pins it on every officer write. Only the administrator corrects it; for anyone else it is
    // left out of the write entirely (resending even the same address re-cleaned could differ and be refused).
    if (!user?.isAdmin && claim?.status !== 'draft') delete cleaned.claimantEmail;   // a draft is its filer's own (claimantEdit)
    const bad = emailProblem(cleaned.claimantEmail) || phoneProblem(cleaned.claimantPhone);
    if (bad) { setActionError({ at: 'contact', text: 'Contact NOT saved: ' + bad }); return; }   // Forensic audit 2026-10-16: the form stays open with the entry
    setActionError({ at: '', text: '' });
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        ...cleaned,
        updatedAt: serverTimestamp(),
      }, user));
      setClaim(prev => ({ ...prev, ...cleaned }));
      setEditingContact(false);
    } catch (e) {
      setActionError({ at: 'contact', text: 'Contact NOT saved: ' + ((e && e.message) || 'the write was refused') + '. Your entry is still here.' });
    }
  };

  const formatDate = (val) => {
    if (!val) return '';
    if (val?.toDate) return val.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    return val;
  };

  const formatAmount = (val) => {
    if (!val && val !== 0) return '';
    return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2 });
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#8AA4BF' }}>
      Loading claim...
    </div>
  );

  if (error) return (
    <div style={{ padding: 32, color: '#FF6B6B' }}>⚠ {error}</div>
  );

  if (!claim) return null;

  const statusLabel = LIFECYCLE_STAGES.find(s => s.key === claim.status)?.label || claim.status;

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="topbar-title">Claim {claim.id}{claim.isExample === true ? <span style={{ marginLeft: 10, padding: '2px 8px', border: '2px solid #E8A020', borderRadius: 4, color: '#E8A020', fontWeight: 800, fontSize: 12, letterSpacing: 2 }}>EXAMPLE ONLY - a test record, not a claim</span> : null}</h1>   {/* Record 662 */}
          <div className="topbar-meta">{claim.claimantName} · {claimCommittee(claim)} · {statusLabel}</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-secondary" onClick={() => onNavigate('claims')}>← Back to Claims</button>
        </div>
      </div>

      <div className="page-content">

        {/* Claim Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Claim Number', value: claim.id, highlight: true },
            { label: 'Status', value: statusLabel, highlight: true },
            { label: 'Committee', value: claimCommittee(claim) },
            { label: 'Amount Claimed', value: formatAmount(claim.amountClaimed), highlight: true },
          ].map(item => (
            <div key={item.label} style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 15, color: item.highlight ? '#E8A020' : '#F4F6F8', fontWeight: 700 }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* RULING 277: the claim check - right, fix, confirm, and the date - before anything else on the page. */}
        <ClaimCheckPanel claim={claim} model={formModel} documentsCount={Array.isArray(documents) ? documents.length : null} /><div style={{ fontSize: 11, color: '#8AA4BF', margin: '-4px 0 10px' }}>This page's one job: get this claim right and served. The rest is under the More buttons further down.</div>{/* Record 650, ruling 276 */}

        {/* Record 599 (Bob, 2026-10-07; section 597): THE 'DEFAULT CLAIM' WATCH - officers and the claimant.
            src\data\carrierDefault.js decides; no agreement profile, presentment or silence -> nothing shows. */}
        <CarrierDefaultBanner claim={claim} show={isOfficer || isClaimantOf(claim, user)} user={user} />   {/* Record 603: user -> header load */}

        {/* Record 690 (suggestion 6): the claim's timeline - open at the top for the member, folded for an officer. */}
        {isClaimantOf(claim, user) && !isOfficer
          ? <ClaimTimeline claimId={claimId} claim={claim} user={user} title="Your claim's timeline" />
          : <MoreSection label="Timeline"><ClaimTimeline claimId={claimId} claim={claim} user={user} /></MoreSection>}

        {/* Record 593 (ruling 205 audit): a blocking NOTICE, not a blocking rule - said before the controls it holds. */}
        {s44Block && (
          <div style={{ background: '#2D1A00', border: '1px solid #E8A020', color: '#E8A020', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 14, fontSize: 12, lineHeight: 1.6 }}>
            ⚠ This member has served written notice under Constitution §44. Your controls to edit, send back and advance
            this claim are held until you record, in the Representation panel below, that you have read the notice.
            Nobody is locked out and no time limit has stopped.
          </div>
        )}

        {/* Lifecycle Stage Bar */}
        <StageBar currentStage={claim.status} onAdvance={handleAdvance} canAdvance={canAdvance && !advancing} advanceTo={advanceTo} blockedReason={advanceBlocked} />
        {actionErrorAt('advance')}{/* Forensic audit 2026-10-16 */}

        {/* THE OFF-PROPERTY LADDER, beside the status ladder (ruling 177 / ISS-39). The status bar
            above says what the Organization is doing; this says where the claim stands with the
            carrier - the answer clock and the appeal ladder. It reads and writes appealActs on the
            claim, scoped by the claim's own rules; appealLadder.js / offProperty.js decide. */}
        <OffPropertyPanel
          claim={claim}
          claimId={claimId}
          user={user}
          documents={documents}
          onActRecorded={(act) => setClaim(prev => ({ ...prev, appealActs: [...(prev.appealActs || []), act] }))}
        />

        {/* BATCH 5 - HOW THIS CLAIM ENDED (rulings 68.1, 61.9, 61.9a, 68.2).
            Opened by the advance into `closure`, because that is the moment the question is real.
            Nothing here is pre-filled: a required field that arrives with a default becomes a place
            people click past, and 61.9a answers that risk by keeping the field short and the blank
            visible rather than by dropping the requirement. */}
        {closureOpen && (
          <div style={{ background: '#0D1B2A', border: '1px solid #E8A020', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', marginBottom: 4 }}>How did this claim end?</div>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 12, lineHeight: 1.6, maxWidth: 720 }}>
              A claim cannot close without this. <b style={{ color: '#DCE7F2' }}>The reason is required on all
              eight outcomes, wins included</b> &mdash; the reason on a win is what tells the next officer
              what worked. Nothing is inferred: a claim closed without an outcome reads as
              <b style={{ color: '#DCE7F2' }}> unrecorded</b> forever, never as a denial.
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {OUTCOMES.map(o => (
                <button key={o.key} onClick={() => setClosure(p => ({ ...p, outcome: o.key }))}
                        style={{ fontSize: 11, padding: '5px 10px', cursor: 'pointer',
                                 border: '1px solid ' + (closure.outcome === o.key ? '#E8A020' : '#2E4A6B'),
                                 background: closure.outcome === o.key ? '#2D1A00' : '#12202F',
                                 color: closure.outcome === o.key ? '#E8A020' : '#8AA4BF',
                                 fontWeight: closure.outcome === o.key ? 700 : 400 }}>
                  {o.label}
                </button>
              ))}
            </div>

            <textarea aria-label="Why did it end this way? What actually decided it." className="form-input" rows={2} value={closure.outcomeReason}
                      placeholder="Why did it end this way? What actually decided it."
                      onChange={e => setClosure(p => ({ ...p, outcomeReason: e.target.value }))}
                      style={{ fontSize: 12, width: '100%', marginBottom: 8 }} />

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <input aria-label="Amount recovered (leave blank if not recorded)" className="form-input" style={{ fontSize: 12, flex: 1, minWidth: 180 }}
                     placeholder="Amount recovered (leave blank if not recorded)"
                     value={closure.amountRecovered}
                     onChange={e => setClosure(p => ({ ...p, amountRecovered: e.target.value }))} />
              {String(closure.amountRecovered).trim() !== '' && (
                <select aria-label="Amount basis" className="form-select" style={{ fontSize: 12, flex: 1, minWidth: 180 }} value={closure.amountBasis}
                        onChange={e => setClosure(p => ({ ...p, amountBasis: e.target.value }))} title="What the figure rests on [R-161]">
                  <option value="">What does the figure rest on?</option>
                  {AMOUNT_BASES.filter(b => b.key !== 'unsourced').map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              )}
              {closure.outcome === 'progressed_to_board' && (
                <input aria-label="Board case number (the join to the docket)" className="form-input" style={{ fontSize: 12, flex: 1, minWidth: 180 }}
                       placeholder="Board case number (the join to the docket)"
                       value={closure.boardCaseNumber}
                       onChange={e => setClosure(p => ({ ...p, boardCaseNumber: e.target.value }))} />
              )}
            </div>
            <div style={{ fontSize: 10, color: '#8AA4BF', marginBottom: 10 }}>
              <b>Blank and zero are different facts.</b> Blank means nobody recorded a figure; zero
              means the claim recovered nothing. A roll-up will never read one as the other.
            </div>

            {closureErrors.length > 0 && (
              <div style={{ background: '#3A1414', border: '1px solid #E85C4A', borderRadius: 4,
                            padding: '8px 12px', marginBottom: 10, fontSize: 11, color: '#FFB3A7' }}>
                {closureErrors.map((e, i) => <div key={i} style={{ marginBottom: 3 }}>{e}</div>)}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={handleClose} disabled={closing}>
                {closing ? 'Recording...' : 'Record outcome and close'}
              </button>
              <button className="btn btn-secondary" style={{ fontSize: 11 }}
                      onClick={() => { setClosureOpen(false); setClosureErrors([]); }}>Cancel</button>
            </div>
          </div>
        )}

        {/* A CLOSED CLAIM SHOWS HOW IT ENDED, or says plainly that nobody recorded it. An
            unrecorded outcome is never rendered as a denial or left blank (68.2). */}
        {claim.status === CLOSING_STAGE && !closureOpen && (
          <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '14px 20px', marginBottom: 20 }}>
            {hasOutcome(claim) ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#27AE60' }}>{outcomeLabel(claim.outcome)}</div>
                <div style={{ fontSize: 12, color: '#DCE7F2', marginTop: 4, lineHeight: 1.6 }}>{claim.outcomeReason}</div>
                <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 6 }}>
                  {typeof claim.amountRecovered === 'number'
                    ? 'Recovered: $' + claim.amountRecovered.toFixed(2) + (amountBasisMeta(claim.amountBasis) ? ' (' + amountBasisMeta(claim.amountBasis).label + (amountBasisMeta(claim.amountBasis).sourced ? '' : ' - not in the sourced total') + ')' : ' (basis not recorded)')
                    : 'No recovered figure was recorded - which is not the same as zero.'}
                  {claim.boardCaseNumber ? '  -  Board case ' + claim.boardCaseNumber : ''}
                  {claim.outcomeRecordedBy ? '  -  recorded by ' + claim.outcomeRecordedBy : ''}
                </div>
                {/* A3.4.3 (section 249): SS85 lines 53-57 - a settlement owes notice to the Locals within 30 days */}
                {settlementNotice(claim) ? (
                  <div style={{ fontSize: 11, marginTop: 8, color: settlementNotice(claim).state === 'overdue' ? '#E06C6C' : settlementNotice(claim).state === 'given' ? '#8AA4BF' : '#E8A020' }}>
                    {settlementNotice(claim).line}
                    {settlementNotice(claim).state !== 'given' && (user?.isAdmin || user?.role === 'general_chair') ? (
                      <span style={{ marginLeft: 10 }}>
                        <input aria-label="Settle notice on" type="date" className="form-input" style={{ display: 'inline-block', width: 160, fontSize: 11, padding: '2px 6px' }} value={settleNoticeOn} onChange={e => setSettleNoticeOn(e.target.value)} title="The date notice was given to the Locals" />
                        <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 8px', marginLeft: 6 }} disabled={!settleNoticeOn} onClick={async () => {
                          try { await updateDoc(doc(db, 'claims', claimId), claimPatch({ settlementNoticeOn: settleNoticeOn, updatedAt: serverTimestamp() }, user)); setClaim(prev => ({ ...prev, settlementNoticeOn: settleNoticeOn })); setActionError({ at: '', text: '' }); }
                          catch (e) { setActionError({ at: 'notice', text: 'Could not record the notice date: ' + (e.code || e.message) }); }   // Forensic audit 2026-10-16: beside the date, not over the page
                        }}>Record the notice date</button>
                      </span>
                    ) : null}
                    {actionErrorAt('notice')}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020' }}>Outcome not recorded</div>
                <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 4, lineHeight: 1.6, maxWidth: 720 }}>
                  This claim was closed before the platform had an outcome field. It reads as
                  <b style={{ color: '#DCE7F2' }}> unrecorded</b> and it is <b style={{ color: '#DCE7F2' }}>not</b> counted
                  as a denial anywhere. Nothing is filled in backwards by inference.<RulingRef user={user} n="68.2" why="An outcome the record never held is not filled in afterwards by inference." />
                </div>
              </>
            )}
          </div>
        )}
        {/* RULING 13 - THE RULES ON THIS CLAIM. Add, or set aside. Never remove.
            Every rule is listed, including every set-aside one, because at a board the question
            "was this theory ever looked at?" has to be answerable from the record. */}
        <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '14px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#DCE7F2' }}>Rules on this claim</div>
            {canHandleRules && !ruleForm.open && (
              <button className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }}
                      onClick={() => { setRuleForm({ open: 'add', ruleId: '', ruleLabel: '', note: '' }); setRuleErrors([]); }}>
                Add a rule
              </button>
            )}
          </div>

          {rulesForDisplay(claim).length === 0 ? (
            <div style={{ fontSize: 12, color: '#8AA4BF' }}>No rules are recorded on this claim.</div>
          ) : (() => {
            // G5.9 / ISS-45 (section 248): two tiers, visually distinct, nothing dropped. The tiers
            // describe the analysis on the CONTROL description, not this claim; a claimant sees one list.
            const row = (r, i) => (
            <div key={r.ruleId || i}
                 style={{ borderBottom: '1px dotted #2E4A6B', padding: '8px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, padding: '2px 6px', whiteSpace: 'nowrap',
                             border: '1px solid ' + (r.setAside ? '#E8A020' : '#27AE60'),
                             color: r.setAside ? '#E8A020' : '#27AE60', borderRadius: 3 }}>
                {r.stateLabel}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: r.setAside ? '#8AA4BF' : '#DCE7F2' }}>
                  {r.ruleLabel || r.ruleId}
                </div>
                {r.addedNote && (
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 2 }}>
                    Added by {r.addedBy}: {r.addedNote}
                  </div>
                )}
                {presenceLabel(r, user) && (
                  <div style={{ fontSize: 11, color: /^INTERMITTENT/.test(presenceLabel(r, user)) ? '#E8A020' : '#8AA4BF', marginTop: 2 }}>
                    {presenceLabel(r, user)}
                  </div>
                )}
                {r.setAside && (
                  <div style={{ fontSize: 11, color: '#E8A020', marginTop: 2 }}>
                    Set aside by {r.setAsideBy}: {r.setAsideNote}
                    <div style={{ color: '#8AA4BF', marginTop: 2 }}>
                      <b>It remains on the claim.</b> Set aside is not removal - the record has to show
                      that this theory was considered.
                    </div>
                  </div>
                )}
              </div>
              {canHandleRules && !r.setAside && !ruleForm.open && (
                <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 8px', color: '#E8A020', borderColor: '#2E4A6B' }}
                        onClick={() => { setRuleForm({ open: 'set_aside', ruleId: r.ruleId, ruleLabel: r.ruleLabel || '', note: '' }); setRuleErrors([]); }}>
                  Set aside
                </button>
              )}
            </div>
            );
            const tiers = tierRules(rulesForDisplay(claim), user);
            if (!tiers.tiered) return tiers.anchors.map(row);
            const block = (key, list, tone) => list.length ? (
              <div key={key}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: tone, margin: '10px 0 2px' }}>{TIER_HEADINGS[key]} ({list.length})</div>
                {list.map(row)}
              </div>
            ) : null;
            return [block('anchors', tiers.anchors, '#27AE60'), block('sometimes', tiers.sometimes, '#E8A020'), block('unmeasured', tiers.unmeasured, '#8AA4BF')];
          })()}

          {ruleForm.open && (
            <div style={{ marginTop: 12, borderTop: '1px solid #2E4A6B', paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#DCE7F2', marginBottom: 6 }}>
                {ruleForm.open === 'add' ? 'Add a rule' : 'Set aside: ' + (ruleForm.ruleLabel || ruleForm.ruleId)}
              </div>

              <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 10, lineHeight: 1.6, maxWidth: 760 }}>
                {ruleForm.open === 'add'
                  ? <>Interpretation of the agreement belongs to the General Chairperson alone. Adding a rule is not interpreting it - say what you are adding and why.<RulingRef user={user} n={13} why="Interpreting the agreement is the General Chairperson's authority alone." /></>
                  : 'The rule STAYS on this claim and stays visible. Setting it aside records that it was considered and why it is not being relied on.'}
                <div style={{ color: '#E8A020', marginTop: 6 }}>
                  <b>This notifies the claimant AND your General Chairperson, in the same write.</b> If
                  either notification cannot be delivered, nothing is saved at all.
                </div>
              </div>

              {ruleForm.open === 'add' && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Rule id</label>
                    <input aria-label="Rule id" className="form-input" style={{ fontSize: 12 }} value={ruleForm.ruleId}
                           onChange={e => setRuleForm(p => ({ ...p, ruleId: e.target.value }))} />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Label as it should read</label>
                    <input aria-label="Label as it should read" className="form-input" style={{ fontSize: 12 }} value={ruleForm.ruleLabel}
                           onChange={e => setRuleForm(p => ({ ...p, ruleLabel: e.target.value }))} />
                  </div>
                </div>
              )}

              <textarea aria-label="Why? This is what a board reads." value={ruleForm.note} rows={2} maxLength={NOTE_MAX}
                        onChange={e => setRuleForm(p => ({ ...p, note: e.target.value }))}
                        placeholder="Why? This is what a board reads."
                        style={{ width: '100%', boxSizing: 'border-box', background: '#12202F',
                                 border: '1px solid #2E4A6B', color: '#DCE7F2', fontSize: 12,
                                 padding: '8px 10px', fontFamily: 'inherit' }} />
              <div style={{ fontSize: 10, color: '#8AA4BF', marginTop: 2 }}>{ruleForm.note.trim().length} / {NOTE_MAX}</div>

              {ruleErrors.length > 0 && (
                <div style={{ border: '1px solid #E06C6C', background: '#2A0F0F', color: '#F4A0A0',
                              padding: '8px 12px', marginTop: 8, fontSize: 12, lineHeight: 1.6 }}>
                  {ruleErrors.map((x, i) => <div key={i}>{x}</div>)}
                </div>
              )}

              <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px' }} disabled={ruleSaving}
                        onClick={() => { setRuleForm({ open: '', ruleId: '', ruleLabel: '', note: '' }); setRuleErrors([]); }}>Cancel</button>
                <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px', color: '#E8A020', borderColor: '#E8A020' }}
                        disabled={ruleSaving} onClick={handleRuleAction}>
                  {ruleSaving ? 'Saving...' : (ruleForm.open === 'add' ? 'Add and notify' : 'Set aside and notify')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RULING 70 - PRESENTMENT. Served on the carrier by whoever served it.
            NO PERMISSION GATE: the claimant, the LC and the GC may all serve, and 70.2 withdrew the
            authority model that was being scoped for this. The control is offered to everyone who
            can see the claim. */}
        <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '14px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: isPresented(claim) ? '#27AE60' : '#8AA4BF' }}>
              Served on the carrier
            </div>
            {!presentOpen && (
              <button className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }}
                      onClick={() => { setPresentOpen(true); setPresentErrors([]); }}>
                {isPresented(claim) ? 'Correct the record' : 'Record the service'}
              </button>
            )}
          </div>

          {!presentOpen && (() => {
            const sum = presentmentSummary(claim);
            return (
              <div style={{ fontSize: 12, color: sum.recorded ? '#DCE7F2' : '#8AA4BF', marginTop: 6, lineHeight: 1.6, maxWidth: 760 }}>
                {sum.text}
                {sum.recorded && sum.unknownMethod && (
                  <div style={{ color: '#E8A020', marginTop: 6, fontSize: 11 }}>
                    <b>The method is recorded as NOT KNOWN.</b> Article 20(c) service disputes turn on
                    the method - if anyone can establish how it was served, correct this.
                  </div>
                )}
              </div>
            );
          })()}

          {presentOpen && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Who served it</label>
                  <select aria-label="Who served it" className="form-select" style={{ fontSize: 12 }} value={present.presentedBy}
                          onChange={e => setPresent(p => ({ ...p, presentedBy: e.target.value }))}>
                    <option value="">Choose...</option>
                    {PRESENTERS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                  </select>
                </div>
                {present.presentedBy === 'other' && (
                  <div style={{ flex: '1 1 200px' }}>
                    <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Their name</label>
                    <input aria-label="Their name" className="form-input" style={{ fontSize: 12 }} value={present.presentedByName}
                           onChange={e => setPresent(p => ({ ...p, presentedByName: e.target.value }))} />
                  </div>
                )}
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Date the carrier received it</label>
                  <input aria-label="Date the carrier received it" className="form-input" type="date" style={{ fontSize: 12 }} value={present.presentedOn}
                         onChange={e => setPresent(p => ({ ...p, presentedOn: e.target.value }))} />
                </div>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>How</label>
                  <select aria-label="How" className="form-select" style={{ fontSize: 12 }} value={present.method}
                          onChange={e => setPresent(p => ({ ...p, method: e.target.value }))}>
                    <option value="">Choose...</option>
                    {METHODS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Proof of receipt</label>
                <input aria-label="Proof of receipt" className="form-input" style={{ fontSize: 12 }} placeholder="e.g. signed for by J. Artz 10/14 2:10 pm; e-mail delivery receipt; certified mail no. 7006 0810 ..."
                       value={present.proof}
                       onChange={e => setPresent(p => ({ ...p, proof: e.target.value }))} />
                <div style={{ fontSize: 10, color: '#6F8AA6', marginTop: 3 }}>The day the carrier received it is the day that counts - this is what shows it.</div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Copies to (comma separated)</label>
                <input aria-label="Copies to (comma separated)" className="form-input" style={{ fontSize: 12 }} placeholder="e.g. Local Chairperson, General Chairperson"
                       value={present.copiesTo}
                       onChange={e => setPresent(p => ({ ...p, copiesTo: e.target.value }))} />
              </div>

              {/* RULING 269: every missing item that carries a consequence, one plain sentence each, LIVE before the
                  record is written. It never blocks; what was shown is recorded with the service. Replaces the single
                  copies warning below (which is one of its items) while the ruling stands. */}
              {RULING_269_CONSEQUENCES && (() => {
                const list = submitConsequences(claim, { ...present, copiesTo: String(present.copiesTo || '').split(',').map(x => x.trim()).filter(Boolean) });
                return list.length === 0 ? null : (
                  <div style={{ border: '1px solid #E8A020', background: '#2D1A00', color: '#E8A020',
                                padding: '8px 12px', marginBottom: 10, fontSize: 11, lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Before you record it - what may follow:</div>
                    {list.map(x => <div key={x.key}>&bull; {x.text}</div>)}
                    <div style={{ color: '#8AA4BF', marginTop: 4 }}>You may still record it. These sentences and your choice are recorded with the service.<RulingRef user={user} n={269} why="Consequences are stated before submit, politely; the warning and the choice are recorded." /></div>
                  </div>
                );
              })()}
              {/* The warning shows LIVE, before the record is written, and it never blocks. */}
              {!RULING_269_CONSEQUENCES && (() => {
                const w = copiesWarning({
                  presentedBy: present.presentedBy,
                  copiesTo: String(present.copiesTo || '').split(',').map(x => x.trim()).filter(Boolean),
                });
                return w.level === 'ok' ? null : (
                  <div style={{ border: '1px solid #E8A020', background: '#2D1A00', color: '#E8A020',
                                padding: '8px 12px', marginBottom: 10, fontSize: 11, lineHeight: 1.6 }}>
                    {w.text}
                  </div>
                );
              })()}

              {presentErrors.length > 0 && (
                <div style={{ border: '1px solid #E06C6C', background: '#2A0F0F', color: '#F4A0A0',
                              padding: '8px 12px', marginBottom: 10, fontSize: 12, lineHeight: 1.6 }}>
                  {presentErrors.map((x, i) => <div key={i}>{x}</div>)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px' }}
                        disabled={presenting} onClick={() => { setPresentOpen(false); setPresentErrors([]); }}>Cancel</button>
                <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px', color: '#27AE60', borderColor: '#27AE60' }}
                        disabled={presenting} onClick={handlePresent}>{presenting ? 'Recording...' : 'Record it'}</button>
              </div>
            </div>
          )}
        </div>

        {/* RULING 72 - SEND IT BACK TO ITS AUTHOR.
            The feature has been decided three times: granted at 67, revoked by 61.6, restored by 72.
            What killed it the second time was 67.3's finding that a returned claim goes INVISIBLE to
            the officer who returned it - so the officer keeps a content-free queue entry on his desk
            (src\pages\LCDesk.js) and the claim keeps its clock. Nothing below decides anything; the
            module does. */}
        {/* Ruling 72.3 item 7 (Record 583): the returned draft is marked INCOMPLETE, with the reason on the stamp. */}
        {(() => { const m = returnMark(claim); return m ? (
          <div style={{ background: '#0D1B2A', border: '2px solid #C0392B', borderRadius: 8, padding: '12px 20px', marginBottom: 20 }}>
            <span style={{ border: '3px solid #C0392B', color: '#C0392B', fontWeight: 800, letterSpacing: 2, padding: '2px 10px', fontSize: 15 }}>{m.mark}</span>
            <div style={{ fontSize: 12, color: '#DCE7F2', marginTop: 8, lineHeight: 1.6, maxWidth: 720 }}>{m.text}<RulingRef user={user} n={72} why="A claim sent back to its author is marked incomplete and keeps its filing window." /></div>
          </div>) : null; })()}
        {returnedNotice && (
          <div style={{ background: '#0D1B2A', border: '1px solid #27AE60', borderRadius: 8, padding: '14px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#27AE60' }}>Sent back to its author</div>
            <div style={{ fontSize: 12, color: '#DCE7F2', marginTop: 6, lineHeight: 1.6, maxWidth: 720 }}>
              It is a draft again, and a draft belongs to its author alone &mdash; so you can
              no longer open or work it. <b>It has not left your desk.</b> It stays in your queue with its
              filing window still counting, showing who holds it and why you sent it. A frozen copy of the claim as you
              returned it, stamped SUPERSEDED, stays readable from that queue entry.<RulingRef user={user} n={56} why="A draft belongs to its author alone until he submits it for representation." />
            </div>
            {onNavigate && (
              <button className="btn btn-secondary" style={{ fontSize: 11, marginTop: 10, padding: '4px 12px' }}
                      onClick={() => onNavigate('desk')}>Back to your desk</button>
            )}
          </div>
        )}

        {canReturn && !returnedNotice && (
          <div style={{ marginTop: 6, marginBottom: 14 }}>
            {!returnOpen ? (
              <div style={{ textAlign: 'right' }}>
                <button className="btn btn-secondary"
                        style={{ fontSize: 11, color: '#8AA4BF', borderColor: '#2E4A6B', padding: '4px 12px' }}
                        disabled={!!s44Block}
                        onClick={() => { setReturnOpen(true); setReturnErrors([]); }}>
                  Send back to its author
                </button>
                {s44Block ? <div style={{ fontSize: 11, color: '#E8A020', marginTop: 4 }}>Held until you acknowledge the member's §44 notice (Representation panel below).</div> : null}
              </div>
            ) : (
              <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '16px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#DCE7F2', marginBottom: 4 }}>
                  Send this claim back to its author
                </div>

                {/* THE WARNING COMES FIRST AND IT IS SHOWN BEFORE HE TYPES, not after he commits.
                    Three distinct levels and they are never merged - an unknown window is its own
                    state and is never rendered as a comfortable one (72.3 item 5). */}
                {returnWarn.level !== 'ok' && (
                  <div style={{ border: '1px solid ' + (returnWarn.level === 'passed' ? '#E06C6C' : '#E8A020'),
                                background: returnWarn.level === 'passed' ? '#2A0F0F' : '#2D1A00',
                                color: returnWarn.level === 'passed' ? '#F4A0A0' : '#E8A020',
                                padding: '10px 12px', margin: '10px 0', fontSize: 12, lineHeight: 1.6 }}>
                    {returnWarn.text}
                  </div>
                )}

                <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 10, lineHeight: 1.6, maxWidth: 720 }}>
                  He is being handed his own claim back and is entitled to know what for. Say what he
                  needs to do. <b style={{ color: '#DCE7F2' }}>Nothing here is pre-filled</b> &mdash; a blank
                  must look like a blank rather than a default nobody read.
                </div>

                <textarea aria-label="What does he need to do?" value={returnReason} onChange={e => setReturnReason(e.target.value)}
                          rows={3} maxLength={REASON_MAX}
                          placeholder="What does he need to do?"
                          style={{ width: '100%', boxSizing: 'border-box', background: '#12202F',
                                   border: '1px solid #2E4A6B', color: '#DCE7F2', fontSize: 12,
                                   padding: '8px 10px', fontFamily: 'inherit' }} />
                <div style={{ fontSize: 10, color: '#8AA4BF', marginTop: 2 }}>
                  {returnReason.trim().length} / {REASON_MAX}
                  {Number(claim.returnCount) > 0 &&
                    <b style={{ color: '#E8A020' }}>{'  -  this claim has already been back ' + claim.returnCount + ' time(s)'}</b>}
                </div>

                {returnErrors.length > 0 && (
                  <div style={{ border: '1px solid #E06C6C', background: '#2A0F0F', color: '#F4A0A0',
                                padding: '8px 12px', marginTop: 10, fontSize: 12, lineHeight: 1.6 }}>
                    {returnErrors.map((x, i) => <div key={i}>{x}</div>)}
                  </div>
                )}

                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px' }}
                          disabled={returning}
                          onClick={() => { setReturnOpen(false); setReturnErrors([]); }}>Cancel</button>
                  <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px', color: '#E8A020', borderColor: '#E8A020' }}
                          disabled={returning}
                          onClick={handleReturn}>{returning ? 'Sending back...' : 'Send it back'}</button>
                </div>
              </div>
            )}
          </div>
        )}

        {isOfficer && !['suspended','archived','closure'].includes(claim.status) && (
          <div style={{ textAlign: 'right', marginTop: 6 }}>
            {maySuspend && (   /* Record 592 (F23) */
            <button className="btn btn-secondary" style={{ fontSize: 11, color: '#E8A020', borderColor: '#E8A020', padding: '4px 12px' }}
              onClick={() => setSuspendModal(true)}>
              ⏸ Suspend / Archive Claim
            </button>
            )}
            {claim.status === 'draft' && onResumeDraft && (
              <button className="btn btn-primary"
                style={{ fontSize: 12, padding: '6px 16px', marginTop: 8, background: '#2A4A2A', borderColor: '#40C070' }}
                onClick={() => onResumeDraft(claimId)}>
                ✏️ Resume & Edit Draft
              </button>
            )}
          </div>
        )}

        {/* Routing — pending-routing badge (all viewers) + reassignment picker (GC/admin) */}
        {isUnrouted && (
          <div style={{ background: '#2A1E0D', border: '1px solid #E8A020', borderRadius: 8, padding: '12px 16px', marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', marginBottom: canRoute ? 8 : 0 }}>
              ⚠ Pending routing — filed and protected, awaiting assignment to a Local Chairperson by the General Chair.
            </div>
            {canRoute && (
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select aria-label="Route to" className="form-select" style={{ maxWidth: 560 }} value={routeTo} onChange={e => setRouteTo(e.target.value)}>
                    <option value="">Select the Local Chairperson…</option>
                    {chairOptions.map(lc => <option key={lc.scopeKey} value={lc.scopeKey}>{lc.label}</option>)}
                  </select>
                  <button className="btn btn-primary" disabled={!routeTo || reassigning} onClick={handleReassign}>
                    {reassigning ? 'Routing…' : 'Route to this Local Chair'}
                  </button>
                </div>
                {narrowedByClaim && (
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 6 }}>
                    Showing the {rosterMatches.length} chair{rosterMatches.length === 1 ? '' : 's'} for {claim.craft} · {claim.railroad}.{' '}
                    <a href="#!" onClick={e => { e.preventDefault(); setShowAllChairs(s => !s); }} style={{ color: '#5B9BD5' }}>
                      {showAllChairs ? 'narrow to matching chairs' : 'show all chairs'}
                    </a>
                  </div>
                )}
                {claim.workLocation && (
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 4 }}>
                    Routing here teaches the map — future {claim.workLocation} claims will auto-route to this chair.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!isUnrouted && canRoute && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8, fontSize: 11, color: '#8AA4BF' }}>
            <span>Routed to {claim.scopeKey}</span>
            <select aria-label="Route to" className="form-select" style={{ maxWidth: 360, fontSize: 11 }} value={routeTo} onChange={e => setRouteTo(e.target.value)}>
              <option value="">Re-route to…</option>
              {chairOptions.map(lc => <option key={lc.scopeKey} value={lc.scopeKey}>{lc.label}</option>)}
            </select>
            <button className="btn btn-secondary" style={{ fontSize: 11 }} disabled={!routeTo || reassigning} onClick={handleReassign}>
              {reassigning ? 'Routing…' : 'Re-route'}
            </button>
          </div>
        )}

        {/* Suspend/Archive Banner */}
        {(claim.status === 'suspended' || claim.status === 'archived') && (
          <div style={{
            background: claim.status === 'archived' ? '#1A0A0A' : '#1A1A0A',
            border: `1px solid ${claim.status === 'archived' ? '#C0392B' : '#E8A020'}`,
            borderRadius: 6, padding: '12px 16px', marginBottom: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: claim.status === 'archived' ? '#FF6B6B' : '#E8A020' }}>
                {claim.status === 'archived' ? '🗄 Claim Archived' : '⏸ Claim Suspended'}
              </div>
              {claim.suspendReason && <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 3 }}>{claim.suspendReason}</div>}
              {claim.suspendedBy && <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2 }}>By {claim.suspendedBy}</div>}
            </div>
            {maySuspend && claim.status !== 'archived' && (   /* Record 592 (F23): was isOfficer */
              <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={handleReactivate}>
                ↩ Reactivate Claim
              </button>
            )}
          </div>
        )}

        {/* Draft Claim Actions */}
        {claim.status === 'draft' && (
          <div style={{ background: '#0A1628', border: '1px solid #E8A020', borderRadius: 6, padding: '12px 16px', marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', marginBottom: 6 }}>
              💾 Draft Claim — Private to You <span style={{ fontWeight: 400, fontSize: 11 }}>(an administrator's read is logged)</span>
            </div>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 10 }}>
              This claim has not been filed. It is private to you. An administrator's read is logged.
              {/* Record 603: named the button below as it is - it notifies the Local Chair only (ruling 89). */}
              Complete and file it, or press <b>Ask my Local Chair for help</b> below - your Local Chair is told you asked; he does not see the draft.
            </div>
            {fileError && (
              <div style={{ background: '#3A1414', border: '1px solid #E85C4A', borderRadius: 4, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: '#FFB3A7', fontWeight: 700 }}>
                {fileError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" style={{ fontSize: 11, background: '#27AE60' }}
                disabled={filing}
                onClick={async () => {
                  if (filing) return;
                  setFiling(true); setFileError('');
                  try {
                    // A draft carries no claim number: the draft-save paths never call
                    // nextClaimNumber(). Assign one at the moment of filing, and never
                    // overwrite one the claim already has.
                    const patch = {
                      status: 'submitted',
                      submittedAt: serverTimestamp(),
                      updatedAt: serverTimestamp(),
                    };
                    // `claim.id` here is the document id, never empty - so the old `!claim.id` test never fired and a draft
                    // filed from this page kept no number (Bob 2026-10-14). Test the stored number by its shape.
                    if (!isIssuedClaimNumber(claim.claimNumber) && !isIssuedClaimNumber(claim.id)) {
                      const cn = await nextClaimNumber(claim.committee || claim.generalCommittee);
                      if (!cn) throw new Error('Could not assign a claim number.');
                      patch.id = cn;
                      patch.title = cn;
                    }
                    await updateDoc(doc(db, 'claims', claimId), claimPatch(patch, user));
                    setClaim(prev => ({ ...prev, ...patch, status: 'submitted' }));
                    // Record 609 (ruling 294): filed - the furtherance submission to the Local Chairperson of record is
                    // recorded by default, once. It never blocks the filing: a refusal comes back as a line, not a throw.
                    setFilingCarriage(await recordCarriageAtFiling({ claimId, claim: { ...claim, ...patch, status: 'submitted' },
                      filerUid: user?.uid || '', roster: LC_ROSTER, rosterSource: seatsOfRecord.source }));
                  } catch(e) {
                    console.error(e);
                    // Never let a failed filing look like a filing (PD-5).
                    setFileError('This claim was NOT filed: ' + (e && e.message ? e.message : 'the save was rejected') +
                      ' Your draft is unchanged. Try again, or contact your Local Chair -- do not assume it went through.');
                  } finally { setFiling(false); }
                }}>
                {filing ? 'Filing...' : '✓ File This Claim'}
              </button>
              <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: '#4A9EE8', color: '#4A9EE8' }}
                onClick={async () => {
                  try {
                    // Ruling 89 (Record 576): the request reaches the Local Chair of the claim's territory, in the same
                    // batch as the flag - both or neither.
                    const n = notifyAssistance({ claimId, scopeKey: claim && claim.scopeKey, committee: claimCommittee(claim),
                      actorName: (user && (user.name || user.email)) || '' });
                    if (!n.ok) { setFileError('Your request was NOT sent: ' + n.errors.join(' ')); return; }
                    const ab = writeBatch(db);
                    ab.update(doc(db, 'claims', claimId), claimPatch({
                      assistanceRequested: true,
                      assistanceRequestedAt: serverTimestamp(),
                      assistanceRequestedBy: user?.name || user?.email,
                      updatedAt: serverTimestamp(),
                    }, user));
                    n.records.forEach(r => ab.set(doc(collection(db, 'notifications')), { ...r, createdAt: serverTimestamp() }));   // Record 592 (F14)
                    await ab.commit();
                    setClaim(prev => ({ ...prev, assistanceRequested: true }));
                    setFileError('');
                    // S-6 (history): an earlier alert said the request was sent to the Local Chair and
                    // General Chair when nothing was sent. Since Records 576/592 the batch above writes
                    // notifyAssistance()'s notifications - to the Local Chair of the claim's territory
                    // only (ruling 89) - in the same commit as the flag. The alert says only that.
                    // Corrected 2026-10-16 (audit): the old comment here still said nothing is sent.
                    alert('Your Local Chair has been notified that you asked for help. He sees the request, not your draft - the draft stays private to you until you file it. An administrator\'s read is logged.');
                  } catch(e) {
                    console.error(e);
                    // Silence on this screen reads as success (PD-5). Say it failed.
                    setFileError('Your assistance request was NOT sent: ' +
                      ((e && e.message) || 'the save was rejected') +
                      '. Nobody has been notified -- try again, or contact your Local Chair directly.');
                  }
                }}>
                🤝 Ask my Local Chair for help
              </button>
              {/* THE 15-DAY EXTENSION BUTTON WAS REMOVED HERE, 2026-08-31. RULING 76 F.
                  It said 15 and wrote 45. It did not EXTEND the window - it REPLACED it, setting an
                  absolute date 45 days from the moment of the press and discarding whatever the
                  agreement had computed; pressed twice it gave 45 again, not 90. It wrote a full
                  instant where computeExpiresAt() writes a date, so daysUntilISO at five officer
                  surfaces was fed two formats. And it said "Extension requested. Your LC/GC has been
                  notified" while applying the change immediately and notifying nobody.

                  Bob's ruling 73, in his own words: "Generally, the answer is yes, but with certain
                  limitations." Ruling 76 adopted the limitations. A is that expiresAt is never
                  editable by anyone, claimant included - the window is the AGREEMENT'S term, and
                  under his doctrine the officer represents the agreement and its terms are not his
                  to move either. F, as replaced: computeExpiresAt() at claimPayload.js:149 is the
                  only writer of expiresAt.

                  WHAT REPLACES IT IS NOT NOTHING, AND IT IS NOT BUILT YET. Ruling 76 B: the claimant
                  MAY elect to proceed on an out-of-time claim - through a separate, explicitly named
                  field, not by moving the date. C: the election is recorded, with who and when and
                  that he was shown the window he was passing. D: the platform states the consequence
                  before he elects. 76.2 is explicit that the SHAPE of B, C and D is not authorised
                  by that ruling and is designed for Bob to read before any of it ships.

                  Removing this is a deletion, and it is step 2 of the order in
                  data\firestore.rules.updatedby_target_2026-08-31. Ruling 76 A cannot be deployed
                  while a live button writes the field the clause forbids. */}
            </div>
            {/* THE THIRD FALSE STATEMENT, AND THE ONE S-6 DID NOT CATCH - FOUND 2026-08-31.
                It read: "Assistance requested - your LC/GC can now view this draft." It could not be
                true. firestore.rules:191, the claims read rule, grants a read to the OWNER always,
                an ADMIN always, and to everyone else ONLY once the claim has left draft - ruling 56,
                enforced rather than asked for. `assistanceRequested` appears NOWHERE in
                firestore.rules. Setting it changes nothing about who may read the claim.
                And unlike the two alerts, this one does not disappear: it sits on the screen for as
                long as the flag is set, telling a member his officers are looking at something they
                are forbidden to open. */}
            {claim.assistanceRequested && (
              <div style={{ fontSize: 11, color: '#40C070', marginTop: 8 }}>✓ Assistance noted on this claim. The draft stays private to you until you file it. An administrator's read is logged.</div>
            )}
          </div>
        )}

        {/* Suspend/Archive Modal */}
        {suspendModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#1B2A4A', border: '1px solid #2E4A6B', borderRadius: 8, padding: 24, width: 420 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#F4F6F8', marginBottom: 12 }}>Suspend or Archive This Claim</div>
              <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 14 }}>
                <b style={{ color: '#E8A020' }}>Suspend</b> — holds the claim pending further action. Can be reactivated.<br />
                <b style={{ color: '#FF6B6B' }}>Archive</b> — permanently removes from active docket. Cannot be reactivated.
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#B8D0E8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                Reason <span style={{ color: '#FF6B6B' }}>* required to archive</span>
              </div>
              <textarea aria-label="Why is this claim being suspended or archived?"
                autoFocus
                style={{ width: '100%', background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 4, padding: '8px 10px', color: '#F4F6F8', fontSize: 12, minHeight: 70, marginBottom: 8, boxSizing: 'border-box', resize: 'vertical' }}
                placeholder="Why is this claim being suspended or archived?"
                value={suspendReason}
                onChange={e => setSuspendReason(e.target.value)}
              />
              {!suspendReason.trim() && (
                <div style={{ fontSize: 11, color: '#E8A020', marginBottom: 12 }}>
                  Archive stays disabled until you give a reason. It is written onto the claim
                  record, and an archived claim cannot be reactivated from this screen.
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setSuspendModal(false)}>Cancel</button>
                <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: '#E8A020', color: '#E8A020' }}
                  onClick={() => handleSuspend('suspended')} disabled={suspending}>
                  ⏸ Suspend
                </button>
                <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: '#FF6B6B', color: '#FF6B6B' }}
                  onClick={() => handleSuspend('archived')} disabled={suspending || !suspendReason.trim()}>
                  🗄 Archive
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Claimant */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="card-title">Claimant Information</div>
            {!editingContact && mayWrite && <button className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }} disabled={!!s44Block} title={s44Block ? 'Held until you acknowledge the member\'s §44 notice (Representation panel below).' : undefined} onClick={() => setEditingContact(true)}>Edit Contact</button>}
            {editingContact && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-primary" style={{ fontSize: 11, padding: '3px 10px' }} onClick={handleSaveContact}>Save</button>
                <button className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setEditingContact(false)}>Cancel</button>
              </div>
            )}
          </div>
          {editingContact && actionErrorAt('contact')}{/* Forensic audit 2026-10-16: shown with the open form it refused */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <Field label="Claimant Name" value={claim.claimantName} />
            {!editingContact ? (
              <>
                <Field label="Cell Phone" value={claim.claimantPhone} />
                <Field label="Street Address" value={claim.claimantStreet} />
                <Field label="Email" value={claim.claimantEmail} />
                <Field label="City" value={claim.claimantCity} />
                <Field label="State / ZIP" value={claim.claimantState && claim.claimantZip ? `${claim.claimantState} ${claim.claimantZip}` : (claim.claimantState || claim.claimantZip)} />
              </>
            ) : (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label className="form-label" style={{ fontSize: 10 }}>Street Address</label>
                  <input aria-label="Street Address" className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={contactForm.claimantStreet} onChange={e => setContactForm(p => ({...p, claimantStreet: e.target.value}))} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 2 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>City</label>
                    <input aria-label="City" className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={contactForm.claimantCity} onChange={e => setContactForm(p => ({...p, claimantCity: e.target.value}))} />
                  </div>
                  <div style={{ flex: 0, minWidth: 60 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>State</label>
                    <input aria-label="State" className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} maxLength={2} value={contactForm.claimantState} onChange={e => setContactForm(p => ({...p, claimantState: e.target.value.toUpperCase()}))} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>ZIP</label>
                    <input aria-label="ZIP" className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} maxLength={10} value={contactForm.claimantZip} onChange={e => setContactForm(p => ({...p, claimantZip: e.target.value}))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>Cell Phone</label>
                    <input aria-label="Cell Phone" className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={contactForm.claimantPhone} onChange={e => setContactForm(p => ({...p, claimantPhone: e.target.value}))} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label" style={{ fontSize: 10 }}>Email Address</label>
                    {/* Forensic audit 2026-10-16: only the administrator may change it (firestore.rules claimantLinkIntact). */}
                    <input aria-label="Email Address" className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={contactForm.claimantEmail} readOnly={!user?.isAdmin && claim?.status !== 'draft'} title={!user?.isAdmin && claim?.status !== 'draft' ? 'The e-mail links the claim to the member\'s account; only the administrator can correct it.' : undefined} onChange={e => setContactForm(p => ({...p, claimantEmail: e.target.value}))} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Jurisdiction */}
        <Section title="Jurisdiction &amp; Agreement">
          <Field label="Craft" value={claim.craft} />
          <Field label="General Committee" value={claimCommittee(claim)} highlight />
          <Field label="Railroad" value={claim.railroad} />
          <Field label="Local Committee" value={claim.localCommittee} />
          <Field label="YM Work Location" value={claim.workLocation} />
          <Field label="Applicable Agreement" value={claim.applicableAgreement} />
          <Field label="Yard Where Event Occurred" value={claim.eventLocation} />
        </Section>

        {/* Incident */}
        <Section title="Incident Details">
          <Field label="Violation Type" value={claim.violationType || (claim.violationTypes || []).join(', ')} highlight />
          <Field label="Incident Date" value={claim.incidentDate ? new Date(claim.incidentDate + 'T12:00:00').toLocaleDateString('en-US', {month:'long', day:'numeric', year:'numeric'}) : '—'} />
          {/* Section 364 (D8): whether a rule was VIOLATED is the General Chairperson's to say
              (rulings 136, 145). The platform shows the basis the claim rests on. */}
          <Field label="Rule / basis of claim" value={claimBasisLabel(claim)} />
          {claim.matchedRules && claim.matchedRules.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, color: '#A8C4DF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
                {claim.applicableAgreement} — Rules Identified
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {/* Forensic audit 2026-10-16: the card read rule.rule only, so a rule an officer ADDED (ruleId/ruleLabel,
                    ruling 13) showed a blank name, and a set-aside rule looked exactly like one relied on. It reads through
                    rulesForDisplay() - the same identity (ruleKey) and state the "Rules on this claim" list uses; `rule` is
                    filled from that identity so the BN class line reads an officer-added rule too. */}
                {rulesForDisplay(claim).map(r0 => ({ ...r0, rule: r0.rule || r0.ruleId })).map((rule, i) => {
                  const eff = resolveRuleText(claim.applicableAgreement, rule.ruleId, claim.eventLocation || claim.workLocation, claim.incidentDate);
                  return (
                  <div key={rule.ruleId || i} style={{ background: '#0A1628', border: '1px solid ' + (rule.setAside ? '#8A6A20' : '#2E4A6B'), borderRadius: 6, padding: '10px 12px', opacity: rule.setAside ? 0.85 : 1 }}>
                    <div style={{ fontSize: 13, color: rule.setAside ? '#8AA4BF' : '#E8A020', fontWeight: 700, marginBottom: 4 }}>{rule.ruleLabel || rule.ruleId}</div>
                    {rule.setAside && (
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: '#E8A020', marginBottom: 4 }}>
                        {rule.stateLabel}{rule.setAsideBy ? ' - by ' + rule.setAsideBy : ''}
                      </div>
                    )}
                    {/* Record 614 (ruling 295): who the BN rule reaches - Protected (seniority before Nov 1, 1994), ROA,
                        or all yardmasters; a derived class says it is a reading. Information, never a block. */}
                    {bnClassLineFor(claim.applicableAgreement, rule) && (
                      <div style={{ fontSize: 10.5, color: '#8AB4E8', marginBottom: 4 }}>{bnClassLineFor(claim.applicableAgreement, rule)}</div>
                    )}
                    {eff && eff.status && eff.status !== 'active' && (
                      <div style={{ fontSize: 11, color: '#FF6B6B', fontWeight: 600, marginBottom: 4 }}>&#9888; {eff.status.replace('_',' ')}{eff.territory ? ' at ' + eff.territory : ''} — should not be cited</div>
                    )}
                    {(eff && eff.text) ? (
                      <div style={{ fontSize: 11, color: '#F4F6F8', borderLeft: '3px solid #E8A020', paddingLeft: 8, marginBottom: 6, whiteSpace: 'pre-wrap' }}>{eff.text.length > 800 ? eff.text.slice(0, 800) + '…' : eff.text}</div>
                    ) : (rule.exactLanguage && (
                      <div style={{ fontSize: 11, color: '#F4F6F8', borderLeft: '3px solid #E8A020', paddingLeft: 8, marginBottom: 6 }}>"{rule.exactLanguage}"</div>
                    ))}
                    {rule.relevance && (
                      <div style={{ fontSize: 11, color: '#40C070' }}>&#x2713; {rule.relevance}</div>
                    )}

                    {/* PD-0 / ruling 228, section 405. Until today this card showed quoted agreement
                        language with NOTHING beside it - no locator, no word that nobody has adopted
                        the agreement, no word that the rule was machine-extracted and never read by a
                        person. A quote shown bare reads as authority. These are three facts the
                        platform already held and did not say. */}
                    {(rule.citations || []).length > 0 && (
                      <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #2E4A6B' }}>
                        {rule.citations.map((c, ci) => (
                          <div key={ci} style={{ fontSize: 10, color: '#8AA4BF', fontFamily: 'monospace' }}>
                            {c.file}{c.line ? ':' + c.line : ''}
                          </div>
                        ))}
                        {rule.citations.some((c) => c.confirmed !== true) && (
                          <div style={{ fontSize: 10.5, color: '#FFD27A', marginTop: 4 }}>
                            Machine-extracted from that file and <b>not yet checked by a person</b>. The
                            words are the file's; that this is the right rule for this claim is not
                            established. Read it at the line above before it goes to a carrier.
                            {' '}No agreement on the platform is confirmed until your General Chairman signs off on it or replaces it.
                          </div>
                        )}
                      </div>
                    )}
                    {/* WITHDRAWN BEFORE IT SHIPPED: the first version of this line read "No General
                        Chairperson has adopted this agreement." True today - and FALSE the day one
                        does, which is exactly the assertion-instead-of-reading that PD-0 forbids.
                        The platform cannot read an adoption state: `agreements` records carry only
                        status active/archived, and AGR-3's agreementCustody.js is imported by no
                        screen (audit finding A16). So this says what is TRUE and stays true - that
                        the platform does not know - and it is replaced by the real state the day
                        A16 is closed. */}
                    <div style={{ fontSize: 10.5, color: '#FFD27A', marginTop: 6 }}>
                      <b>This platform does not record whether a General Chairperson has adopted this
                      agreement.</b> Until it does, treat it as a reference copy: the words are the
                      file's, and the authority behind them is not established here.
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          <Field label="Time of Incident" value={claim.incidentTime12 ? `${claim.incidentTime12} ${claim.incidentTimePeriod || ''} (${claim.incidentTime24 || ''})`.trim() : (claim.incidentTime24 || '')} />
          <Field label="Yard of Incident" value={claimYard(claim)} />
        </Section>

        {/* Parties */}
        <Section title="Parties Involved">
          <Field label="Other employee involved — Name" value={claim.violatorName || claim.subjectName || '—'} />
          <Field label="Subject Roster #" value={claim.violatorRosterNumber || '—'} />
          <Field label="Subject Job Symbol / Job Symbol" value={claim.violatorAssignment || '—'} />
          <Field label="Violator Rest Days" value={claim.violatorRestDays} />
          <Field label="Other Persons Involved" value={claim.personsInvolved} />
          <Field label="Witnesses" value={claim.witnesses} />
          {claim.violatorIsNonAgreement && (
            <div style={{ gridColumn: '1 / -1', background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 6, padding: '10px 14px', color: '#FF6B6B', fontSize: 12 }}>
              ⚠ Non-agreement employee — Letter of Non-Acquiescence on file
            </div>
          )}
        </Section>

        {/* Description */}
        {claim.description && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">Description</div></div>
            <p style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.6, margin: 0 }}>{claim.description}</p>
          </div>
        )}

        {/* Section 510 (2026-09-29): the claimant's Box 3 answers, each beside the question as he saw it. Saved from
            tonight on; claims filed earlier never had them written (they were lost on save), so they show nothing. */}
        {Array.isArray(claim.box3Answers) && claim.box3Answers.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">Claimant's Answers</div></div>
            {claim.box3Answers.map((a, i) => (
              <div key={(a && a.id) || i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#8AA4BF' }}>{a && a.q}</div>
                <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{a && a.answer}</div>
              </div>
            ))}
          </div>
        )}

        {/* Section 512: the claim itself, as it prints - right under what the claimant wrote. */}
        <ClaimFormPanel claim={claim} user={user} claimId={claimId} onModel={setFormModel} onPatched={(p) => setClaim(prev => ({ ...prev, ...p }))} />

        {/* What the claim is worth - rulings 88 / 93 / 94. Drawn by ClaimWorthPanel from claimValue.js;
            renders nothing for a railroad the platform holds no agreement for. Sits ABOVE the filer's
            own figures so the PLACEHOLDER banner is read before any number underneath it. */}
        <MoreSection label="What the claim is worth"><ClaimWorthPanel claim={claim} user={user} /></MoreSection>

        {/* Valuation */}
        <MoreSection label="Valuation"><Section title="Valuation">
          <Field label="Claim Type" value={claim.isOvertimeClaim ? 'Overtime (O/T)' : 'Straight Time (S/T)'} />
          <Field label="Hourly Rate S/T" value={claim.hourlyRateST ? `$${claim.hourlyRateST}` : ''} />
          <Field label="Total Hours Claimed" value={claim.totalHoursClaimed} />
          <Field label="Hourly Rate O/T" value={claim.hourlyRateOT ? `$${claim.hourlyRateOT}` : ''} />
          <Field label="Total Amount Claimed" value={formatAmount(
                  claim.totalAmountOT && claim.totalAmountOT > 0 ? claim.totalAmountOT :
                  claim.amountClaimed && claim.amountClaimed > 0 ? claim.amountClaimed :
                  (claim.hourlyRateST && claim.totalHoursClaimed) ? claim.hourlyRateST * 1.5 * claim.totalHoursClaimed : 0
                )} highlight />
          <Field label="Priority" value={claim.priority} />
        </Section></MoreSection>

        {/* History - ruling 86 */}
        <MoreSection label="History of changes"><ClaimHistory claimId={claimId} claim={claim} /></MoreSection>

        {/* ONB-6b: WHO CARRIES THIS CLAIM. Above the review record and the letters deliberately -
            an officer about to work a claim should meet the member's §44 notice before he meets
            anything else on the page (ruling 205). */}
        {/* T5 item 8 - RULING 29: the claimant's always-open channel. Not behind More: it is always open. */}
        <ClaimantChannelPanel claim={claim} claimId={claimId} user={user} />
        <ExtensionRequestPanel claim={claim} claimId={claimId} user={user} />   {/* T5 item 6 */}

        <MoreSection label="Representation (section 44)" forceOpen={!!s44Block} why="Open because a served notice waits for your acknowledgment."><SelfRepresentationPanel claim={claim} claimId={claimId} user={user} onAckState={setS44Block} /></MoreSection>   {/* Record 593: onAckState; Record 650: behind More, opens itself when it blocks */}

        {/* Record 594 (ruling 135 audit): CP-1 carriage - who carries this claim (carriageLine), the claimant's
            submission to her representative, and the return by agreement (143). Record 609 (ruling 294): the furtherance
            submission is recorded by default at filing (above, once); the panel itself moves carriage only on her click. The representative is the holder
            of record of this claim's Local Chairperson seat, from the same roster the page already reads. */}
        {filingCarriage && filingCarriage.line && (
          <div style={{ fontSize: 12, color: filingCarriage.ok ? '#27AE60' : '#E8A020', marginBottom: 8 }}>{filingCarriage.line}</div>
        )}
        {lateArrivalFlag(claim) && (   /* Record 680 (audit 2026-08-27 3.6): information, never a block */
          <div style={{ fontSize: 12, color: '#FF6B6B', border: '1px solid #FF6B6B', borderRadius: 4, padding: '6px 10px', marginBottom: 8 }}>
            <strong>{lateArrivalFlag(claim).text}.</strong> {NOT_A_BLOCK}
          </div>
        )}
        <CarriagePanel claim={claim} claimId={claimId} user={user}
          roster={LC_ROSTER} rosterSource={seatsOfRecord.source} /><WorkingCopyBar claimId={claimId} claim={claim} user={user} onPublished={(p) => setClaim(prev => ({ ...prev, ...p }))} /><InclusionPanel claimId={claimId} claim={claim} user={user} onPatched={(p) => setClaim(prev => ({ ...prev, ...p }))} /><CarrierSenderPanel claimId={claimId} claim={claim} user={user} onPatched={(p) => setClaim(prev => ({ ...prev, ...p }))} /><ProgressionDecisionPanel claim={claim} claimId={claimId} user={user} />{/* Ruling 313 */}{/* Record 652 (ruling 305.12) */}{/* Record 647 (ruling 305.10) */}

        {/* RULING 270 (2026-10-02): the notice to other members the claim would affect. Records the notice only - M-3
            governs representation. Renders nothing while the ruling's constant is off. */}
        <AffectedMembersPanel claim={claim} claimId={claimId} user={user}
          onRecorded={(e) => setClaim(prev => ({ ...prev, affectedMembersNotice: [...(prev.affectedMembersNotice || []), e] }))} />

        {/* Review record - ruling 69 */}
        <MoreSection label="Review record"><ReviewPanel claim={{ ...claim, docId: claimId }} user={user} /></MoreSection>

        {/* Initial notice letter group - 6a-09, ruling 130 (officers; not on a draft). Hidden, section 512. */}
        {SHOW_LETTER_GROUP && <LetterGroupPanel claim={claim} user={user} />}

        {/* Documents */}
        <ClaimDocuments
          claimId={claimId}
          claim={claim}
          documents={documents}
          user={user}
          onDocumentsUpdated={(added, removed) => {
            if (added) setDocuments(prev => [...prev, ...added]);
            if (removed) setDocuments(prev => prev.filter(d => d.storagePath !== removed.storagePath));
          }}
        />

        {/* Record 613 (Bob 10/8), ruling 157: every recording on the claim, whoever made it - a carrier-produced one is
            stored and linked; one made by the organization or a member is pointed to (who holds it, where it is). */}
        <MediaTracker parentKind="claim" parentId={claimId} parent={claim} documents={documents} user={user} />

        {/* Case Notes & Communications Log */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">Case Notes &amp; Communications Log</div>
            <div style={{ fontSize: 11, color: '#8AA4BF' }}>Permanent record — no deletions permitted</div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 12, padding: '8px 12px', background: '#0A1628', borderRadius: 6, border: '1px solid #2E4A6B' }}>
            The member's notes and the acts on the claim are shown to everyone on it. An officer's note stays between the union's officers until the claim goes back to the member or to the carrier, or 30 days after it is written - whichever comes first - and then the member reads it.<RulingRef user={user} n={305} why="An officer's note is the union's while it works the claim; the member reads it when the claim comes back to her or goes to the carrier, or within 30 days." />
          </div>
          {officerNotesError ? <div className="alert-banner" style={{ marginBottom: 12 }}>⚠ {officerNotesError}</div> : null}{/* Forensic audit 2026-10-16 */}
          {officerNoteRows.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              {officerNoteRows.slice().sort((a, b) => (toMs(a.addedAt) || 0) - (toMs(b.addedAt) || 0)).map(n => (
                <div key={n.id} style={{ background: '#0A1628', border: '1px solid ' + (n.visibleToMember ? '#2E4A6B' : '#8A6A20'), borderRadius: 6, padding: '10px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: n.role === 'general_chair' ? '#E8A020' : '#4A9EE8', textTransform: 'uppercase', letterSpacing: 1, border: '1px solid currentColor', borderRadius: 3, padding: '1px 6px' }}>{n.role === 'general_chair' ? 'General Chair' : n.role === 'local_chair' ? 'Local Chair' : 'Administrator'}</span>
                    <span style={{ fontSize: 11, color: '#8AA4BF' }}>{n.addedBy}{toMs(n.addedAt) ? ' · ' + new Date(toMs(n.addedAt)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.5 }}>{n.text}</div>
                  {writesOfficerNotes ? <div style={{ fontSize: 11, color: n.visibleToMember ? '#8AA4BF' : '#E8A020', marginTop: 6 }}>{visibilityLine(n)}</div> : null}
                </div>
              ))}
            </div>
          ) : null}
          {claim.notes && claim.notes.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {claim.notes.map((n, i) => {
                const isGC = n.role === 'general_chair';
                const isLC = n.role === 'local_chair';
                // Forensic audit 2026-10-16: an administrator's note (role 'admin' - App.js isAdmin is role === 'admin', and
                // handleClose / presentment stamp user.role) fell through to the Member badge. A platform tester's likewise.
                const isAdminNote = n.role === 'admin';
                const isTesterNote = n.role === 'tester';
                const roleLabel = isGC ? 'General Chair' : isLC ? 'Local Chair' : isAdminNote ? 'Administrator' : isTesterNote ? 'Platform Tester' : 'Member';
                const roleBadgeColor = isGC ? '#E8A020' : isLC ? '#4A9EE8' : (isAdminNote || isTesterNote) ? '#8AA4BF' : '#40C070';
                return (
                  <div key={i} style={{ background: '#0A1628', border: `1px solid ${isGC ? '#E8A020' : '#2E4A6B'}`, borderRadius: 6, padding: '10px 14px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: roleBadgeColor, textTransform: 'uppercase', letterSpacing: 1, border: `1px solid ${roleBadgeColor}`, borderRadius: 3, padding: '1px 6px' }}>{roleLabel}</span>
                      <span style={{ fontSize: 11, color: '#8AA4BF' }}>{n.addedBy} · {new Date(n.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.5 }}>{n.text}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ color: 'var(--steel)', fontSize: 13, marginBottom: 14 }}>No notes yet. Add the first note below.</div>
          )}
          {mayWrite && writesOfficerNotes ? (   /* Record 634 (ruling 305.7): Bob - "that must be made absolutely clear to the representatives" */
            <div style={{ fontSize: 12, color: '#F4F6F8', background: '#3A2A08', border: '1px solid #E8A020', borderRadius: 6, padding: '8px 12px', marginBottom: 8, lineHeight: 1.5 }}>
              <b style={{ color: '#E8A020' }}>Read this before you write.</b> {OFFICER_NOTE_WARNING}
              <label style={{ display: 'block', marginTop: 6, cursor: 'pointer' }}><input aria-label="Share this note with the member now" type="checkbox" checked={shareNow} onChange={e => setShareNow(e.target.checked)} style={{ marginRight: 6 }} />Share this note with the member now</label>
            </div>
          ) : null}
          {mayWrite && (   /* Record 592 (F24) */
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <textarea aria-label="Share this note with the member now" className="form-textarea" placeholder="Add a case note — document events, decisions, communications, or directives..." value={note} onChange={e => setNote(e.target.value)} rows={3} style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={handleAddNote} disabled={savingNote || !note.trim()} style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
              {savingNote ? 'Saving...' : 'Add Note'}
            </button>
          </div>
          )}
          {actionErrorAt('note')}{/* Forensic audit 2026-10-16 */}
          {noteSuccess && <div style={{ color: '#27AE60', fontSize: 12, marginTop: 6 }}>✓ Note saved</div>}
        </div>


        {/* Board Awards */}<MoreSection label="Board awards">
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">Board Awards</div>
            <div style={{ fontSize: 11, color: '#8AA4BF' }}>PLB, 4th Division, SBA, and other relevant arbitration awards</div>
          </div>
          {claim.boardAwards && claim.boardAwards.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              {claim.boardAwards.map((a, i) => (
                <div key={i} style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#E8A020' }}>{a.boardType}</span>
                    <span style={{ fontSize: 12, color: '#F4F6F8', fontWeight: 600 }}>Award #{a.awardNumber}</span>
                    {a.date && <span style={{ fontSize: 11, color: '#8AA4BF' }}>{a.date}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#F4F6F8', marginBottom: a.link ? 6 : 0 }}>{a.summary}</div>
                  {a.link && <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#4A9EE8' }}>→ View Award</a>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--steel)', fontSize: 13, marginBottom: 12 }}>No board awards cited yet.</div>
          )}
          {mayWrite && (   /* Record 592 (F24): was any officer, at any rung */
            <AddBoardAward claimId={claimId} user={user} onAdded={(award) => setClaim(prev => ({ ...prev, boardAwards: [...(prev.boardAwards || []), award] }))} />
          )}
        </div></MoreSection>

        {/* Exhibits */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">Exhibits</div>
            <div style={{ fontSize: 11, color: '#8AA4BF' }}>Supporting evidence — auto-linked to uploaded documents</div>
          </div>
          {/* Auto-linked documents */}
          {documents && documents.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>From Uploaded Documents</div>
              {documents.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#E8A020', minWidth: 70 }}>Exhibit {i + 1}</span>
                  <span style={{ fontSize: 12, color: '#F4F6F8', flex: 1 }}>{d.name}</span>
                  <span style={{ fontSize: 11, color: '#8AA4BF' }}>{d.type}</span>
                  <a href={d.downloadURL || '#'} onClick={viewStoredFile(storage, d.downloadURL, d.storagePath)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#4A9EE8' }}>View</a>
                </div>
              ))}
            </div>
          )}
          {/* Manual exhibits */}
          {claim.exhibits && claim.exhibits.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>Additional Exhibits</div>
              {claim.exhibits.map((ex, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#E8A020', minWidth: 70 }}>Exhibit {(documents?.length || 0) + i + 1}</span>
                  <span style={{ fontSize: 12, color: '#F4F6F8', flex: 1 }}>{ex.description}</span>
                  {ex.link && <a href={ex.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#4A9EE8' }}>View</a>}
                </div>
              ))}
            </div>
          )}
          {(!documents || documents.length === 0) && (!claim.exhibits || claim.exhibits.length === 0) && (
            <div style={{ color: 'var(--steel)', fontSize: 13, marginBottom: 12 }}>No exhibits yet. Upload documents above to auto-populate exhibits.</div>
          )}
        </div>
        {/* Filed Info */}
        <div className="card">
          <div className="card-header"><div className="card-title">Filing Information</div></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <Field label="Filed By" value={claim.filedBy} />
            <Field label="Filed At" value={formatDate(claim.filedAt || claim.createdAt)} />
            <Field label="Local Chairperson" value={lcForClaim} />
            <Field label="Last Updated" value={formatDate(claim.updatedAt)} />
          </div>
          {/* WO-001 S8 (section 225): the General Chairperson of record on the day this claim was filed - from officeTerms, never from a custom claim. */}
          <OfficeOfRecord committee={claimCommittee(claim)} date={claim.filedAt || claim.createdAt} label="General Chairperson of record on the filing date" />
          {claim.scopeKey ? <OfficeOfRecord committee={claim.scopeKey} date={claim.filedAt || claim.createdAt} label="Local Chairperson of record on the filing date" /> : null}
        </div>

      </div>
    </div>
  );
}
